// @ts-strict-ignore
import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, DestroyRef, ElementRef, EmbeddedViewRef, HostListener, input, OnChanges, OnDestroy, OnInit, output, Signal, SimpleChanges, viewChild, viewChildren, ViewContainerRef } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ITableProvider, TableProviderType } from './table-provider';
import { CellCoords } from './handsontable-3rdparty/coords';
import { CellRange } from './handsontable-3rdparty/range';
import { ChangeMultilineAction } from './handsontable-undoredo/change-multiline-action';
import { MergeAndDivideAction } from './handsontable-undoredo/merge-and-divide-action';
import { isConnectionSpanData, ISpanData, SpanDataFactory } from './span-data';
import { LineSpanInfo } from './line-span-info';
import { LabelFormat } from './label-format';
import { LabelEditorUtils } from './label-editor-utils';
import { QuillTableEditorFactory } from './quill-table-editor-factory';
import { ConstraintCheckUtils } from './constraint-check-validation/constraint-check-utils';
import { ConstraintCheckResults } from './constraint-check-validation/constraint-check-results';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Subject, Subscription } from 'rxjs';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { Timer } from '@shared/timer';
import { UserSettings } from '@user/model/user-settings';
import { WindowService } from '@shared/window/window.service';
import { ArticleTechnicalInfoBundle, ArticleTechnicalInfoVO, ArticleVO } from '@article/model/vo/article.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { StringUtils } from '@shared/util/string-utils';
import { LocaleUtils } from '@shared/util/locale-utils';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { LabelEditorPENOptions } from './assistant/label-editor-assistant.component';
import { LabelEditorAssistantUtils } from './assistant/label-editor-assistant-utils';
import { ProjectModel } from '../../../model/project.model';
import { LabelEditorChanges } from './aside-label-editor/aside-label-editor.component';
import { CookieStorage } from '@shared/cookie-storage';
import { UserModel } from '@user/model/user.model';
import { UserUtils } from '@user/service/user-utils';
import { LabelEditorUndoRedoData } from '@shared/undo/operation/label-editor-sequence-operation';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { LabelStripJoiner } from '@project/components/label/replace-labeling-helper/label-strip-joiner';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { LabelEditorStateManager } from './service/label-editor-state.manager';
import { UserComponentPresenter } from '@user/components/user-component.presenter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { SvgIconName } from '@shared/svg-icon/svg-icon-name';
import { debounceTime, filter } from 'rxjs/operators';
import { ConfiguratorParameters } from '@3d-scene/configurator-parameters/configurator-parameters';
import { IsProjectType } from '@project/model/is-project-type';
import { SessionStorageUtils } from '@shared/util/session-storage.utils';
import Quill from 'quill';
import Delta from 'quill-delta';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { DirectPrintingService } from '../direct-printing.service';

export enum LabelEditorMode {
  CLOSED = 'CLOSED',
  EDITING = 'EDITING',
  APPLYING_CHANGES = 'APPLYING_CHANGES',
  TEXT_CHECK_FAILED = 'TEXT_CHECK_FAILED',
}

export interface TableRenderingState {
  isEnabled: boolean;
}

@Component({
  standalone: false,
  selector: 'app-label-editor',
  templateUrl: 'label-editor.component.html',
  styleUrls: ['./label-editor.component.scss'],
})
export class LabelEditorComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private static readonly TOOLTIP_TIMER_DELAY: number = 500;

  private static readonly TEXT_ROTATION_OPTIONS: Object[] = [
    {
      value: 0,
      textRotation: 0,
      label: '0',
    },
    {
      value: 90,
      textRotation: 90,
      label: '90',
    },
    {
      value: 180,
      textRotation: 180,
      label: '180',
    },
    {
      value: 270,
      textRotation: 270,
      label: '270',
    },
  ];

  private static readonly UNSELECTED_TEXT_ROTATION_OPTIONS: Object[] = [
    {
      value: 1,
      textRotation: 0,
      label: '0',
    },
    {
      value: 91,
      textRotation: 90,
      label: '90',
    },
    {
      value: 181,
      textRotation: 180,
      label: '180',
    },
    {
      value: 271,
      textRotation: 270,
      label: '270',
    },
  ];

  private static TEXT_COMPRESSION_OPTIONS: Object[] = null;
  private static UNSELECTED_TEXT_COMPRESSION_OPTIONS: Object[] = null;
  public readonly LabelEditorMode = LabelEditorMode;

  // provided data / options of label editor
  public tableProviders = input<ITableProvider[] | undefined> (undefined);
  public targetState = input<string>('basicControl'); // basicControl or advancedControl
  public showDefaultLabelingBorderExplanation = input<boolean>(false);
  public markingSystemLabelModeEnabled = input<boolean>(false);
  public articleTechnicalInfoBundles = input<ArticleTechnicalInfoBundle[] | undefined>(undefined); // used for marking systems
  public selectedColorString = input<string | undefined>(); // used for marking systems
  public selectAllCellsOnInit = input.required<boolean>();
  public error = input<{title: string, message: string}>(undefined);
  public totalHeight = input.required<number>();

  public modeChangedEmitter = output<LabelEditorMode>();
  public saveChangesEmitter = output<LabelEditorChanges>();

  // custom tooltip handling
  private tooltipComponent: ComponentRef<TooltipComponent> = null;
  private tooltipTimer: Timer;
  private showToolTipOnMouseEnterEditor: boolean = true;
  private scrolled$ = new Subject<void>();

  // view
  public isAdvancedMode: boolean = false;
  public isNavigationTabsHidden: boolean = false;
  public isChangeMarkingModeAvailable: boolean = false;
  public isDirectPrintingModeEnabled: boolean = false;
  public isSelectedCellsDescriptionEnabled: boolean = false;
  public containsTextCheckErrors: boolean = false;
  public containsTextCheckStrictConformityErrors: boolean = false;
  public mode: LabelEditorMode = LabelEditorMode.EDITING;

  // editor component
  private labelEditor: Signal<ElementRef> = viewChild('labelEditor');
  private textRotationSelect: Signal<ElementRef> = viewChild('textRotationSelect');
  private textRotationSpan: Signal<ElementRef> = viewChild('textRotationSpan');
  private textCompressionSelect: Signal<ElementRef> = viewChild('textCompressionSelect');
  private textCompressionSpan: Signal<ElementRef> = viewChild('textCompressionSpan');
  private symbolSelectionSpan: Signal<ElementRef> = viewChild('symbolSelectionSpan');
  private symbolPESelectionSpan: Signal<ElementRef> = viewChild('symbolPESelectionSpan');
  private symbolNSelectionSpan: Signal<ElementRef> = viewChild('symbolNSelectionSpan');
  private autoFormatByTextCheckCheckBox: Signal<ElementRef> = viewChild('autoFormatByTextCheckCheckBox');
  private verticalAlignTopButton: Signal<ElementRef> = viewChild('verticalAlignTopButton');
  private verticalAlignMiddleButton: Signal<ElementRef> = viewChild('verticalAlignMiddleButton');
  private verticalAlignBottomButton: Signal<ElementRef> = viewChild('verticalAlignBottomButton');
  private horizontalAlignLeftButton: Signal<ElementRef> = viewChild('horizontalAlignLeftButton');
  private horizontalAlignCenterButton: Signal<ElementRef> = viewChild('horizontalAlignCenterButton');
  private horizontalAlignRightButton: Signal<ElementRef> = viewChild('horizontalAlignRightButton');
  private multilineSelects: Signal<readonly ElementRef[]> = viewChildren('multilineSelect');

  public textRotationOptions: Object[];
  public textCompressionOptions: Object[];
  public symbolOptions: Object[];
  public symbolPEOptions: Object[];
  public symbolNOptions: Object[];

  public multilineSelectData: Object[];
  public currentMaxMultilineCount: number = 1;
  private maxAvailableMultilineCount: number = 1;
  public selectedMultilineCount: number = 0;

  // tab preselection
  public lastSelectedTabType: string = null;
  public lastSelectedNonDirectPrintingTabType: string = null;
  public lastSelectedSeparateTabPositionNumber: string = null;
  public lastSelectedSeparateTabArticleNumber: string = null;
  public currentTabIndex: number = 0;

  // label editor assistant
  private isLabelEditorAssistantOpen: boolean = false;

  // quick marking
  public isQuickMarkingAvailable: boolean = false;
  public isQuickMarkingFromFocused: boolean = false;
  private _quickMarkingFrom: string = '1';
  public quickMarkingTo: string = 'n';
  public isPENOptionsAvailable: boolean = false;
  public withPE: boolean = true;
  public withN: boolean = true;
  private _selectedPESymbol: string = null;
  private _selectedNSymbol: string = null;

  // marking system infos
  public markingSystemOptions: Object[];
  private _selectedMarkingSystemValue: number;
  public selectedMarkingSystemDescriptionText: string = '';
  public selectedMarkingSystemImage: string = '';

  // dialog service subscription
  private bsModalServiceOnShowSubscription: Subscription;
  private bsModalServiceOnHideSubscription: Subscription;

  // editor component
  private editor: Quill;
  private lastSelectedRangeInEditor;
  private isEditorFormatUpdateInProgress: boolean = false;
  private changedEditorFormatName: "color" | "align" | "size" | null = null;
  private selectedTextRotation: number = 0;
  private selectedTextCompression: number = 100;
  private selectedVerticalAlign: string = LabelFormat.VALIGN_DEFAULT;
  private _selectedSymbol: number = null;
  public quillTableEditor: any;
  public isAutoFormatByTextCheckSelected: boolean = true;
  public currentlyEditedSpanData: ISpanData = null;
  public currentlyEditedSpanDataCheckResults: ConstraintCheckResults = null;
  public isPasteInLabelEditorAllowed: boolean = false;

  // table component: variable is untyped because ts-definition from module file is missing existing functions in regard to undo/redo
  private table: any;
  private tables = new Map<string, any>();
  public tableProvider: ITableProvider = null;
  private lastSelectedRangeInTable;
  public tableRenderingState: TableRenderingState = { isEnabled: true }; // need to control table rendering ourselves to improve performance
  public isAssistantEnabled: boolean = false;
  public isMergeCellsEnabled: boolean = false;
  public isJoinLabelStripsEnabled: boolean = false;
  public isCopyCellsEnabled: boolean = false;
  public isDivideCellsEnabled: boolean = false;
  public isClearCellsEnabled: boolean = false;
  public isAddSymbolEnabled: boolean = false;
  public isCopyFromExcelActivated: boolean = false;
  public isPasteFromExcelDone: boolean = false;
  public lastSelectedRangeInTableBeforeCopyFromExcel;

  // table rendering
  private initHandler = () => this.init();

  // table event handlers
  public afterTableChangeHandler = () => this.afterTableChange();
  public afterTableScrollHandler = () => this.afterTableScroll();
  public afterTableBeginEditingHandler = (row, column) => this.afterTableBeginEditing(row, column);
  public afterTablePasteHandler = (data, coords) => this.afterTablePaste(data, coords);
  public beforeTableOnCellMouseDownHandler = (event, coords, td, controller) => this.beforeTableOnCellMouseDown(event, coords, td, controller);
  public afterTableOnCellMouseOverHandler = (event, coords, td) => this.afterTableOnCellMouseOver(event, coords, td);
  public afterTableOnCellMouseOutHandler = (event, coords, td) => this.afterTableOnCellMouseOut(event, coords, td);
  public shouldDeselectCellsOnOutsideClickHandler = () => this.shouldDeselectCellsOnOutsideClick();
  private disableHandsontableShotcutsHandler = (event) => this.disableHandsontableShotcuts(event);

  // used in undo/redo change-multiline and merge-and-divide actions
  public changeMultilineCountHandler = (cells, newMultilineCount, restoreCellCoordsStringToOldColSpanMap) =>
    this.changeMultilineCount(cells, newMultilineCount, restoreCellCoordsStringToOldColSpanMap);
  public mergeAndDivideCellsHandler = (cellRange, isMerge, restoreCellCoordsStringToOldColSpanMap) =>
    this.mergeAndDivideCells(cellRange, isMerge, restoreCellCoordsStringToOldColSpanMap);

  // editor event handlers
  private editorSelectionChangeHandler = (range, oldRange, source) => this.editorSelectionChange(range, oldRange, source);
  private editorTextChangeHandler = (delta, oldContents, source) => this.editorTextChange(delta, oldContents, source);
  private delayedEditorFormatUpdateHandler = () => this.delayedEditorFormatUpdate();
  private deleteTextInEditorUpdateHandler = (range) => this.deleteTextInEditorUpdate();

  // document event handlers
  private documentKeyDownHandler = (event) => this.documentKeyDown(event);

  // tooltip event handlers
  private onTooltipTimerCompleteHandler = () => this.onTooltipTimerComplete();

  // modal service event handlers
  private onDialogShowHandler = () => this.onDialogShow();
  private onDialogHideHandler = () => this.onDialogHide();

  private notificationAsideLabelEditorSubscription: Subscription;

  constructor(
    private readonly _cookieStorage: CookieStorage,
    private readonly bsModalService: BsModalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly userSettings: UserSettings,
    private readonly userModel: UserModel,
    private readonly localeResourceManager: LocaleResourceManager,
    private readonly textSizeCheckManager: TextSizeCheckManager,
    private readonly alertHelper: AlertHelper,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly windowService: WindowService,
    private readonly projectModel: ProjectModel,
    private readonly labelStripJoiner: LabelStripJoiner,
    private readonly labelingUtils: LabelingUtils,
    private readonly containerService: ContainerService,
    private readonly labelEditorStateManager: LabelEditorStateManager,
    private readonly _userComponentPresenter: UserComponentPresenter,
    private readonly destroyRef: DestroyRef,
    private readonly projectComponentPresenter: ProjectComponentPresenter,
    private readonly _labelEditorStateManager: LabelEditorStateManager,
    private readonly articleContainerUtils: ArticleContainerUtils,
    private readonly directPrintingService: DirectPrintingService,
    private readonly spanDataFactory: SpanDataFactory,
  ) {
    this.scrolled$.pipe(takeUntilDestroyed(), debounceTime(200))
      .subscribe(() => this.refocusEditor());
  }

  public useStrictTextCheckConformity(): boolean {
    return this.targetState() === 'advancedControl';
  }

  public get selectedMarkingSystemValue(): number {
    return this._selectedMarkingSystemValue;
  }

  public get useUniformDirectPrintingFontSize(): boolean {
    return this.directPrintingService.useUniformDirectPrintingFontSize;
  }

  public async setUseUniformDirectPrintingFontSize(value: boolean): Promise<void> {
    if (value) {
      await this.directPrintingService.activateUniformDirectPrinting();
    } else {
      await this.directPrintingService.deactivateUniformDirectPrinting();
    }
    this.projectModel.notifyCurrentProjectChanged();
  }

  public set selectedMarkingSystemValue(newValue: number) {
    this._selectedMarkingSystemValue = newValue;
    if (this.markingSystemOptions && this._selectedMarkingSystemValue >= 0 && this._selectedMarkingSystemValue <= this.markingSystemOptions.length) {
      this.selectedMarkingSystemDescriptionText = this.markingSystemOptions[this.selectedMarkingSystemValue]['description'];
      this.selectedMarkingSystemImage = this.markingSystemOptions[this.selectedMarkingSystemValue]['image'];
    }
  }

  get quickMarkingFrom(): string {
    return this._quickMarkingFrom;
  }

  set quickMarkingFrom(value: string) {
    this._quickMarkingFrom = value;
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), true);
    if (ArrayUtils.isNotEmpty(selectedCells)) {
      const parsedValue: number = value ? parseInt(this.quickMarkingFrom, 10) : 0;
      this.quickMarkingTo = (parsedValue + selectedCells.length - 1).toString();
    } else {
      this.quickMarkingTo = 'n';
    }
  }

  public get selectedPESymbol(): string {
    return this._selectedPESymbol;
  }

  public set selectedPESymbol(value: string) {
    this._selectedPESymbol = value;
    // needs to be delayed because ql editor updates ui after data binding
    setTimeout(() => {
      this.resetSymbolSelectionActiveState(this.symbolPESelectionSpan());
    }, 100);
  }

  public get selectedNSymbol(): string {
    return this._selectedNSymbol;
  }

  public set selectedNSymbol(value: string) {
    this._selectedNSymbol = value;
    // needs to be delayed because ql editor updates ui after data binding
    setTimeout(() => {
      this.resetSymbolSelectionActiveState(this.symbolNSelectionSpan());
    }, 100);
  }

  private getPreselectedMarkingSystemOptionValueByColorString(colorString: string): number {
    const articleTechnicalInfoBundles = this.articleTechnicalInfoBundles();
    if (articleTechnicalInfoBundles && colorString) {
      for (let i: number = 0; i < articleTechnicalInfoBundles.length; i++) {
        const techInfo: ArticleTechnicalInfoVO = articleTechnicalInfoBundles[i].articleTechnicalInfoVO;
        if (techInfo) {
          for (const property of techInfo.articleTechnicalInfoVOs) {
            if (property.name === ArticleTechnicalInfoPropertyDef.COLOR) {
              for (const propValue of property.values) {
                if (propValue.locale === 'en_US' || propValue.locale === 'en_GB') {
                  if (colorString.toLocaleLowerCase() === propValue.stringValue.toLocaleLowerCase()) {
                    return i;
                  }
                }
              }
            }
          }
        }
      }
    }
    return -1;
  }

  private getMarkingSystemOptionLabel(articleTechnicalInfoBundle: ArticleTechnicalInfoBundle): string {
    if (articleTechnicalInfoBundle && articleTechnicalInfoBundle.articleTechnicalInfoVO) {
      for (const property of articleTechnicalInfoBundle.articleTechnicalInfoVO.articleTechnicalInfoVOs) {
        if (property.name === ArticleTechnicalInfoPropertyDef.COLOR) {
          return PropertyUtils.getLocalizedPropertyValueAsStringByLocaleChain(property, this.localeResourceManager.getLocaleChain());
        }
      }
    }
    return 'undefined';
  }

  private getMarkingSystemOptionImage(articleTechnicalInfoBundle: ArticleTechnicalInfoBundle): string {
    if (!articleTechnicalInfoBundle) {
      return '';
    }
    return ArticlePreviewUtils.getPreviewImage(articleTechnicalInfoBundle.articleVO);
  }

  private initMarkingSystemOptions(): void {
    this.markingSystemOptions = [];
    if (this.markingSystemLabelModeEnabled()) {
      const articleTechnicalInfoBundles = this.articleTechnicalInfoBundles();
      if (articleTechnicalInfoBundles) {
        for (let i: number = 0; i < articleTechnicalInfoBundles.length; i++) {
          const articleTechnicalInfoBundle: ArticleTechnicalInfoBundle = articleTechnicalInfoBundles[i];
          if (!articleTechnicalInfoBundle) {
            continue;
          }
          const colorLabel: string = this.getMarkingSystemOptionLabel(articleTechnicalInfoBundle);
          const articleImage: string = this.getMarkingSystemOptionImage(articleTechnicalInfoBundle);
          let articleDescription: string = '';
          if (articleTechnicalInfoBundle.articleVO) {
            articleDescription = articleTechnicalInfoBundle.articleVO.articleNo;
            if (StringUtils.isEmpty(articleDescription)) {
              articleDescription = '';
            } else {
              articleDescription += ' ';
            }
            articleDescription += LocaleUtils.getValueByLocaleChain(
              articleTechnicalInfoBundle.articleVO.displayName,
              this.localeResourceManager.getLocaleChain()
            );
          }
          const markingSystemOption: Object = {
            value: i,
            label: colorLabel,
            image: articleImage,
            description: articleDescription,
            article: articleTechnicalInfoBundle.articleVO,
          };
          this.markingSystemOptions.push(markingSystemOption);
        }
      }
      this.selectedMarkingSystemValue = this.getPreselectedMarkingSystemOptionValueByColorString(this.selectedColorString());
    }
  }

  private initTextCompressionOptions(): void {
    if (LabelEditorComponent.TEXT_COMPRESSION_OPTIONS === null) {
      LabelEditorComponent.TEXT_COMPRESSION_OPTIONS = [];
      LabelEditorComponent.UNSELECTED_TEXT_COMPRESSION_OPTIONS = [];
      for (let i: number = 1; i <= 100; i++) {
        const compressionOption: Object = {
          value: i,
          textCompression: i,
          label: i.toString(10),
        };
        const unselectedCompressionOption: Object = {
          value: 100 + i,
          textCompression: i,
          label: i.toString(10),
        };
        LabelEditorComponent.TEXT_COMPRESSION_OPTIONS.push(compressionOption);
        LabelEditorComponent.UNSELECTED_TEXT_COMPRESSION_OPTIONS.push(unselectedCompressionOption);
      }
    }
  }

  private setMaxAvailableMultilineCount(): void {
    this.maxAvailableMultilineCount = 1;
    if (ArrayUtils.isNotEmpty(this.tableProviders())) {
      this.tableProviders().forEach((tableProvider) => {
        this.maxAvailableMultilineCount = Math.max(this.maxAvailableMultilineCount, tableProvider.getMaxAvailableMultilineCount());
      });
    }
  }

  private createMultilineSelects(): void {
    this.multilineSelectData = [];
    if (this.maxAvailableMultilineCount > 1) {
      for (let i: number = 1; i <= this.maxAvailableMultilineCount; i++) {
        const multilineOptions: Object[] = [];
        for (let j: number = 1; j <= i; j++) {
          const multilineOption: Object = {
            value: j,
            multilineCount: j,
            label: i === 1 ? '-' : j + '',
          };
          multilineOptions.push(multilineOption);
        }
        this.multilineSelectData.push({ maxMultilineCount: i, options: multilineOptions });
      }
    }
  }

  public ngOnInit(): void {
    this.initToolTipSupport();
    this.initTextCompressionOptions();
    this.setMaxAvailableMultilineCount();
    this.createMultilineSelects();
    this.initMarkingSystemOptions();
    this.setNavigationTabsVisibility();
    this.lastSelectedTabType = this._cookieStorage.lastSelectedLabelEditorTabType;
    this.lastSelectedNonDirectPrintingTabType = this._cookieStorage.lastSelectedNonDirectPrintingLabelEditorTabType;
    this.lastSelectedSeparateTabPositionNumber = this._cookieStorage.lastSelectedSeparateLabelEditorTabPositionNumber;
    this.lastSelectedSeparateTabArticleNumber = this._cookieStorage.lastSelectedSeparateLabelEditorTabArticleNumber;
    this.setMarkingMode(true);
    this.setCurrentTabIndex();
    this.setSelectedCellsDescriptionVisibility();
    this.textRotationOptions = LabelEditorComponent.TEXT_ROTATION_OPTIONS;
    this.textCompressionOptions = LabelEditorComponent.TEXT_COMPRESSION_OPTIONS;
    this.symbolOptions = LabelEditorUtils.createSymbolOptions([
      0xe011,
      [0xe070, 0xe073],
      [0xe01e, 0xe01f],
      0xe062,
      0xe027,
      [0xe021, 0xe022],
      0xe06c,
      [0xe069, 0xe06b],
      0x252c,
      0x2534,
      [0xe023, 0xe026],
      0xe060,
      [0xe065, 0xe068],
      [0xe000, 0xe010],
      [0xe012, 0xe017],
      [0xe019, 0xe01c],
      0xe030,
      [0xe040, 0xe044],
      [0xe049, 0xe04a],
      0xe04e,
      [0xe057, 0xe05c],
      0x03a9,
    ]);
    const lastSelectedSymbolCharCode: number = this.userSettings.lastSelectedSymbolCharCodeInLabelEditor;
    this.selectedSymbol = isNaN(lastSelectedSymbolCharCode) ? this.symbolOptions[0]['value'] : lastSelectedSymbolCharCode;

    this.withPE = this._cookieStorage.withPESymbolInLabelEditor;
    this.symbolPEOptions = LabelEditorUtils.createSymbolOptions([
      'PE',
      0xe011,
      [0xe070, 0xe073],
      [0xe01e, 0xe01f],
      0xe062,
      0xe027,
      [0xe021, 0xe022],
      0xe06c,
      [0xe069, 0xe06b],
      0x252c,
      0x2534,
      [0xe023, 0xe026],
      0xe060,
      [0xe065, 0xe068],
      [0xe000, 0xe010],
      [0xe012, 0xe017],
      [0xe019, 0xe01c],
      0xe030,
      [0xe040, 0xe044],
      [0xe049, 0xe04a],
      0xe04e,
      [0xe057, 0xe05c],
      0x03a9,
    ]);
    const lastSelectedPESymbol: string = this.userSettings.lastSelectedPESymbolInLabelEditor;
    this.selectedPESymbol = lastSelectedPESymbol ? lastSelectedPESymbol : this.symbolPEOptions[0]['value'];

    this.withN = this._cookieStorage.withNSymbolInLabelEditor;
    this.symbolNOptions = LabelEditorUtils.createSymbolOptions([
      0x004e,
      0xe011,
      [0xe070, 0xe073],
      [0xe01e, 0xe01f],
      0xe062,
      0xe027,
      [0xe021, 0xe022],
      0xe06c,
      [0xe069, 0xe06b],
      0x252c,
      0x2534,
      [0xe023, 0xe026],
      0xe060,
      [0xe065, 0xe068],
      [0xe000, 0xe010],
      [0xe012, 0xe017],
      [0xe019, 0xe01c],
      0xe030,
      [0xe040, 0xe044],
      [0xe049, 0xe04a],
      0xe04e,
      [0xe057, 0xe05c],
      0x03a9,
    ]);
    const lastSelectedNSymbol: string = this.userSettings.lastSelectedNSymbolInLabelEditor;
    this.selectedNSymbol = lastSelectedNSymbol ? lastSelectedNSymbol : this.symbolNOptions[0]['value'];

    const SizeStyle = Quill.import('attributors/style/size');
    // Quill is a bit strange and uses type "unknown" here. It is implemented exactly as in the example...
    // @ts-ignore
    SizeStyle.whitelist = [
      '3px',
      '4px',
      '5px',
      '6px',
      '7px',
      '8px',
      '9px',
      '10px',
      '11px',
      '12px',
      '14px',
      '16px',
      '18px',
      '20px',
      '22px',
      '24px',
      '26px',
      '28px',
      '36px',
      '48px',
      '72px',
    ];
    // Quill is a bit strange and uses type "unknown" here. It is implemented exactly as in the example...
    // @ts-ignore
    Quill.register(SizeStyle, true);

    const icons = Quill.import("ui/icons");

    // For icon keys see https://github.com/slab/quill/blob/main/packages/quill/src/ui/icons.ts
    icons["color"] = this.getColorIcon();

    icons["align"] = {
      "": this.getIcon("align-left"),
      "center": this.getIcon("align-center"),
      "right": this.getIcon("align-right"),
    };
    icons["align-vertical"] = {
      "bottom": this.getIcon("vertical-align-bottom"),
      "middle": this.getIcon("vertical-align-center"),
      "top": this.getIcon("vertical-align-top"),
    };

    const AlignStyle = Quill.import('attributors/style/align');
    // Quill is a bit strange and uses type "unknown" here. It is implemented exactly as in the example...
    // @ts-ignore
    Quill.register(AlignStyle, true);
    this.isAdvancedMode = this.targetState() === 'advancedControl';
    this.bsModalServiceOnShowSubscription = this.bsModalService.onShow.subscribe(this.onDialogShowHandler);
    this.bsModalServiceOnHideSubscription = this.bsModalService.onHide.subscribe(this.onDialogHideHandler);
    this.notificationAsideLabelEditorSubscription = this.labelEditorStateManager.fullScreenChange$.subscribe(() => this.renderTable());
    this._labelEditorStateManager.editorDataChange$
      .pipe(takeUntilDestroyed(this.destroyRef), filter((data) => !data))
      .subscribe(() => this.close());
  }

  public ngAfterViewInit(): void {
    document.addEventListener('keydown', this.documentKeyDownHandler, true);

    this.editor = this.createQuillEditor();

    this.quillTableEditor = QuillTableEditorFactory.create(this, this.labelEditor(), this.editor);

    setTimeout(this.initHandler, 250); // delay time due to aside-menu transition speed
  }

  private createQuillEditor(): Quill {
    const bindings = {
      d1: {
        key: 'backspace',
        handler: this.deleteTextInEditorUpdateHandler,
      },
      d2: {
        key: 'delete',
        handler: this.deleteTextInEditorUpdateHandler,
      },
    };
    const quill = new Quill('#labelEditor', {
      theme: 'snow',
      modules: {
        toolbar: '#labelEditorToolbar',
        keyboard: {
          bindings: bindings,
        },
      },
    });
    for (const nodeType of [ Node.ELEMENT_NODE, Node.TEXT_NODE ]) {
      quill.clipboard.addMatcher(nodeType, (node, delta) => {
        const newDelta = new Delta();
        for (const op of delta.ops) {
          newDelta.insert(op.insert, this.editor.getFormat());
        }
        return newDelta;
      });
    }
    quill.root.spellcheck = false;
    quill.on('selection-change', this.editorSelectionChangeHandler);
    quill.on('text-change', this.editorTextChangeHandler);
    return quill;
  }

  private init(): void {
    if (ArrayUtils.isNotEmpty(this.tableProviders())) {
      this.updateTextCheckErrorsState();
      this.selectTab(this.currentTabIndex);
      this.table.render();
    }
    this.resetEditor();
    if (this.selectAllCellsOnInit()) {
      this.selectAllCells();
    }
    if (this.windowService.getModalsCount() > 0) {
      this.onDialogShow();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tableProviders && !changes.tableProviders.isFirstChange()) {
      this.setMarkingMode(false);
      this.setCurrentTabIndex();
      this.tables.clear();
      this.setMaxAvailableMultilineCount();
      this.createMultilineSelects();
      this.initMarkingSystemOptions();
      this.setNavigationTabsVisibility();

      setTimeout(() => {
        this.init();
        this.updateToolbar();
      }, 100);
    }
  }

  private getIcon(icon: SvgIconName) {
   return `
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      style="width: 18px; height: 18px"
      color="currentColor">
      <use xlink:href="/ng-assets/img/icons/${icon}.svg#${icon}"
      href="/ng-assets/img/icons/${icon}.svg#${icon}"></use>
      </svg>`
  }

  /** The color icon needs special treatment to support changing the color indicator */
  private getColorIcon() {
    return `<svg id="quill-colorpicker"
    style="width: 18px; height: 18px"
    viewBox="0 0 24 24"
    fill="none"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlns:svg="http://www.w3.org/2000/svg">
    <defs
    />
    <path
      d="M 16.25,20.26067 H 7.87 v -1.5 h 3.47 V 3.5 H 4.5 V 6.08 H 3 V 2 H 21.2 V 6.09 L 19.7,6.08 V 3.5 h -6.86 v 15.26067 h 3.41 z"
    fill="currentColor"
    />
    <line
      class="ql-color-label ql-stroke ql-transparent"
      x1="1.36" x2="22.7"
      y1="23" y2="23"></line>
      </svg>`
  }

  private updateToolbar(): void {
    const theme: any = (this.editor as any).theme;
    theme.pickers.forEach((picker) => {
      (picker.container as HTMLElement).remove(); // remove html elements created by quill for previous data
      (picker.select as HTMLElement).style.display = ''; // restore visibility of original html elements
    });
    theme.pickers = [];
    theme.extendToolbar(theme.modules.toolbar); // create quill html elements for new data and hides original ones
  }

  private selectAllCells(scrollToCell: boolean = false): void {
    if (ArrayUtils.isNotEmpty(this.tableProvider.table) && !this.error()) {
      this.table.selectCell(0, 0, this.table.countRows() - 1, this.table.countCols() - 1, scrollToCell);
      this.setFocusOnTable();
    }
  }

  public get selectedSymbol(): number {
    return this._selectedSymbol;
  }

  public set selectedSymbol(value: number) {
    this._selectedSymbol = value;
    // needs to be delayed because ql editor updates ui after data binding
    setTimeout(() => {
      this.resetSymbolSelectionActiveState(this.symbolSelectionSpan());
    }, 100);
  }

  public updateSelectedSymbol(strValue: string): void {
    // select html element in template will update selected symbol by providing symbol (code) with datatype of string:
    this.selectedSymbol = Number(strValue);
  }

  public addSymbol(): void {
    if (!this.selectedSymbol) {
      return;
    }
    if (this.isEditorOpen()) {
      let range = this.editor.getSelection(true);
      if (!range) {
        range = this.lastSelectedRangeInEditor;
      }
      if (range) {
        let format = null;
        if (range.index === 0) {
          format = this.editor.getFormat(0, 1);
        }
        this.editor.deleteText(range.index, range.length, 'api');
        this.editor.insertText(range.index, String.fromCharCode(this.selectedSymbol), 'api');
        if (format) {
          this.editor.formatText(range.index, range.length, format, 'api');
        }
        this.performValidationOnCurrentTextInEditor();
      }
    } else {
      const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected());
      const cellDataList = [];
      const symbol: string = String.fromCharCode(this.selectedSymbol);
      for (const selectedCell of selectedCells) {
        const spanData: ISpanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
        const data = this.table.getDataAtCell(selectedCell[0], selectedCell[1]);
        /* Using label editor is not fast enough (especially in IE 11):
        this.labelEditor.nativeElement.querySelector('.ql-editor').innerHTML = data;
        this.editor.update('api');
        const format = this.editor.getFormat(0, 1);
        this.editor.deleteText(0, this.editor.getLength(), 'api');
        this.editor.insertText(0, String.fromCharCode(this.selectedSymbol), 'api');
        this.editor.formatText(0, this.editor.getLength(), format, 'api');
        this.editor.update('api');
        const newData: string = this.labelEditor.nativeElement.querySelector('.ql-editor').innerHTML;*/
        const newData: string = LabelEditorUtils.replaceTextInHTML5Data(data, symbol);
        const cellData = [
          selectedCell[0],
          selectedCell[1],
          LabelEditorUtils.validateAndApplyHTML5Data(newData, spanData, this.useStrictTextCheckConformity(), this.localeResourceManager),
        ];
        cellDataList.push(cellData);
      }
      if (cellDataList.length > 0) {
        this.table.setDataAtCell(cellDataList);
      }
    }
  }

  public undo(): void {
    this.table.undo();
  }

  public redo(): void {
    this.table.redo();
  }

  public onPESymbolChange(): void {
    this.withPE = true;
  }

  public onNSymbolChange(): void {
    this.withN = true;
  }

  public createAssistantPopup(): void {
    const backupSelectionInTable = this.table.getSelectedRange();
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), true);
    this.isLabelEditorAssistantOpen = true;
    const selectedPENOptions: LabelEditorPENOptions = {
      withPE: this.withPE,
      withN: this.withN,
      selectedPESymbol: this.selectedPESymbol,
      selectedNSymbol: this.selectedNSymbol,
    };
    this.projectComponentPresenter
      .openLabelEditorAssistantPopup(
        this.tableProvider,
        selectedCells,
        true,
        this.isPENOptionsAvailable,
        this.symbolPEOptions,
        this.symbolNOptions,
        selectedPENOptions,
        this.quickMarkingFrom,
        this.quickMarkingTo
      )
      .then((result) => {
        this.isLabelEditorAssistantOpen = false;
        if (result === null) {
          // label editor assistant dialog was cancelled
          this.table.selectCells(backupSelectionInTable, false);
        } else {
          this.setAutomaticMarkingData(result.cellLabelList);
          this.withPE = result.selectedPENOptions.withPE;
          this.withN = result.selectedPENOptions.withN;
          this.selectedPESymbol = result.selectedPENOptions.selectedPESymbol;
          this.selectedNSymbol = result.selectedPENOptions.selectedNSymbol;
          this.table.selectCells(backupSelectionInTable, false);
        }
      });
  }

  private setAutomaticMarkingData(cellLabelList): void {
    const cellDataList = [];
    for (const cellLabel of cellLabelList) {
      const spanData: ISpanData = this.tableProvider.getSpanData(cellLabel[0], cellLabel[1]);
      const data = this.table.getDataAtCell(cellLabel[0], cellLabel[1]);
      const newData: string = LabelEditorUtils.replaceTextInHTML5Data(data, cellLabel[2]);
      const cellData = [
        cellLabel[0],
        cellLabel[1],
        LabelEditorUtils.validateAndApplyHTML5Data(newData, spanData, this.useStrictTextCheckConformity(), this.localeResourceManager),
      ];
      cellDataList.push(cellData);
    }
    if (cellDataList.length > 0) {
      this.table.setDataAtCell(cellDataList);
    }
  }

  public applyQuickMarking(): void {
    const backupSelectionInTable = this.table.getSelectedRange();
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), true);
    const selectedPENOptions: LabelEditorPENOptions = {
      withPE: this.isPENOptionsAvailable && this.withPE,
      withN: this.isPENOptionsAvailable && this.withN,
      selectedPESymbol: this.selectedPESymbol,
      selectedNSymbol: this.selectedNSymbol,
    };
    const cellLabelList = LabelEditorAssistantUtils.getCellLabelListForQuickNumericMarking(
      this.tableProvider,
      selectedCells,
      parseInt(this.quickMarkingFrom, 10),
      selectedPENOptions,
      this.labelingUtils,
      this.containerService,
    );
    this.setAutomaticMarkingData(cellLabelList);
    this.table.selectCells(backupSelectionInTable, false);
  }

  private deleteTextInEditorUpdate() {
    return true;
  }

  private afterTableChange(): void {
    this.mode = LabelEditorMode.APPLYING_CHANGES;
    this.modeChangedEmitter.emit(this.mode);
    this.updateCustomFormatButtons();
    this.updateMultilineSelect();
    this.updateMergeDivideCopyClearCellsAvailability();
    this.updateJoinLabelStripsAvailability();
    this.updateQuickMarking();
    this.updateTextCheckErrorsState();
    this.resetEditor(); // needs to be last to include changes to custom toolbar (see calls above) in toolbar in update (reset) of editor
    // save needs to be delayed because it uses undo operations and Undo/Redo Plugin also listenes and updates to table change
    // and there are no priorities for listeners
    setTimeout(() => {
      if (this.table && this.table.isUndoAvailable() && (this.mode !== LabelEditorMode.TEXT_CHECK_FAILED || this.isDirectPrintingModeEnabled)) {
        this.save();
      } else if (this.mode !== LabelEditorMode.TEXT_CHECK_FAILED) {
        this.mode = LabelEditorMode.EDITING;
        this.modeChangedEmitter.emit(this.mode);
      }
      this.cdRef.markForCheck();
    }, 100);
  }

  private beforeTableOnCellMouseDown(event, coords, td, controller): void {}

  public afterTableSelectionEnd(): void {
    this.lastSelectedRangeInTable = this.table.getSelectedRange();
    this.updateCustomFormatButtons();
    this.updateMultilineSelect();
    this.updateMergeDivideCopyClearCellsAvailability();
    this.updateJoinLabelStripsAvailability();
    this.updateAssistentAvailability();
    this.updateAddSymbolAvailability();
    this.updateQuickMarking();
    this.resetEditor(); // needs to be last to include changes to custom toolbar (see calls above) in toolbar in update (reset) of editor
  }

  private getCommonSpanDataForCellRanges(cellRanges, onlyIncludeEnabledCells: boolean = true): ISpanData {
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, cellRanges, onlyIncludeEnabledCells);
    const commonSpanData: ISpanData = this.spanDataFactory.createSpanData();
    commonSpanData.labelFormat.direction = -1;
    commonSpanData.labelFormat.verticalAlign = 'unset';
    commonSpanData.compression = -1;
    commonSpanData.autoFormatByTextCheck = null;
    for (const selectedCell of selectedCells) {
      const spanData: ISpanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
      // text rotation (aka direction)
      if (commonSpanData.labelFormat.direction === -1) {
        commonSpanData.labelFormat.direction = spanData.labelFormat.direction;
      } else if (commonSpanData.labelFormat.direction !== spanData.labelFormat.direction) {
        commonSpanData.labelFormat.direction = -2;
      }
      // vertical align
      if (commonSpanData.labelFormat.verticalAlign === 'unset') {
        commonSpanData.labelFormat.verticalAlign = spanData.labelFormat.verticalAlign;
      } else if (commonSpanData.labelFormat.verticalAlign !== spanData.labelFormat.verticalAlign) {
        commonSpanData.labelFormat.verticalAlign = null;
      }
      // text compression
      if (commonSpanData.compression === -1) {
        commonSpanData.compression = spanData.compression;
      } else if (commonSpanData.compression !== spanData.compression) {
        commonSpanData.compression = -2;
      }
      // auto format by text check
      if (commonSpanData.autoFormatByTextCheck === null) {
        commonSpanData.autoFormatByTextCheck = spanData.autoFormatByTextCheck;
      } else if (commonSpanData.autoFormatByTextCheck !== spanData.autoFormatByTextCheck) {
        commonSpanData.autoFormatByTextCheck = undefined;
      }
    }
    if (commonSpanData.labelFormat.verticalAlign === 'unset') {
      commonSpanData.labelFormat.verticalAlign = null;
    }
    if (commonSpanData.autoFormatByTextCheck === undefined) {
      commonSpanData.autoFormatByTextCheck = null;
    }
    return commonSpanData;
  }

  private updateCustomFormatButtons(): void {
    if (this.editor) {
      if (this.isEditorOpen()) {
        // vertical align (we only use leadingSpanData to make vertical align highlight behave similar to horizontal align from quill)
        this.selectedVerticalAlign = this.currentlyEditedSpanData.labelFormat.verticalAlign;
        if (this.verticalAlignBottomButton) {
          this.verticalAlignBottomButton()?.nativeElement.classList.remove('ql-active');
        }
        if (this.verticalAlignTopButton) {
          this.verticalAlignTopButton()?.nativeElement.classList.remove('ql-active');
        }
        if (this.verticalAlignMiddleButton) {
          this.verticalAlignMiddleButton()?.nativeElement.classList.remove('ql-active');
        }
        if (this.selectedVerticalAlign === LabelFormat.VALIGN_BOTTOM && this.verticalAlignBottomButton) {
          this.verticalAlignBottomButton()?.nativeElement.classList.add('ql-active');
        } else if (this.selectedVerticalAlign === LabelFormat.VALIGN_TOP && this.verticalAlignTopButton) {
          this.verticalAlignTopButton()?.nativeElement.classList.add('ql-active');
        } else if (this.selectedVerticalAlign === LabelFormat.VALIGN_MIDDLE && this.verticalAlignMiddleButton) {
          this.verticalAlignMiddleButton()?.nativeElement.classList.add('ql-active');
        }
      } else {
        const selectedCellRange = this.table ? this.table.getSelectedLast() : null;
        if (selectedCellRange && selectedCellRange.length >= 2) {
          const leadingSpanData: ISpanData = this.tableProvider.getSpanData(selectedCellRange[0], selectedCellRange[1]);
          // we want to include disabled cells, so formatting work even in case lead cell is disabled
          const commonSpanData: ISpanData = this.getCommonSpanDataForCellRanges(this.table.getSelected(), false);
          if (leadingSpanData) {
            // text rotation
            this.selectedTextRotation = leadingSpanData.labelFormat.direction;
            if (commonSpanData.labelFormat.direction < 0) {
              this.textRotationOptions = LabelEditorComponent.UNSELECTED_TEXT_ROTATION_OPTIONS;
              if (this.textRotationSelect()) {
                this.textRotationSelect().nativeElement.value = -1;
              }
            } else {
              // needs to be set here directly on native html element, so changes are visible for editor on update/reset
              // update of bindings to elements would happen later & be invisible for editor, which replaces select with custom elements
              if (this.textRotationOptions === LabelEditorComponent.UNSELECTED_TEXT_ROTATION_OPTIONS) {
                this.textRotationOptions = LabelEditorComponent.TEXT_ROTATION_OPTIONS;
                if (this.textRotationSelect()) {
                  this.textRotationSelect().nativeElement.value = this.selectedTextRotation + 1;
                }
              } else {
                if (this.textRotationSelect()) {
                  this.textRotationSelect().nativeElement.value = this.selectedTextRotation;
                }
              }
            }

            // text compression
            this.selectedTextCompression = leadingSpanData.compression;
            if (commonSpanData.compression < 0) {
              this.textCompressionOptions = LabelEditorComponent.UNSELECTED_TEXT_COMPRESSION_OPTIONS;
              if (this.textCompressionSelect()) {
                this.textCompressionSelect().nativeElement.value = -1;
              }
            } else {
              // needs to be set here directly on native html element, so changes are visible for editor on update/reset
              // update of bindings to elements would happen later & be invisible for editor, which replaces select with custom elements
              if (this.textCompressionOptions === LabelEditorComponent.UNSELECTED_TEXT_COMPRESSION_OPTIONS) {
                this.textCompressionOptions = LabelEditorComponent.TEXT_COMPRESSION_OPTIONS;
                if (this.textCompressionSelect()) {
                  this.textCompressionSelect().nativeElement.value = this.selectedTextCompression + 100;
                }
              } else {
                if (this.textCompressionSelect()) {
                  this.textCompressionSelect().nativeElement.value = this.selectedTextCompression;
                }
              }
            }

            // auto format by text check (check all selected cells)
            this.isAutoFormatByTextCheckSelected = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), false)
              .map((selected) => this.tableProvider.getSpanData(selected[0], selected[1]))
              .every((i) => i.autoFormatByTextCheck);
            if (this.autoFormatByTextCheckCheckBox()) {
              this.autoFormatByTextCheckCheckBox().nativeElement.checked = this.isAutoFormatByTextCheckSelected;
            }

            // vertical align (we only use leadingSpanData to make vertical align highlight behave similar to horizontal align from quill)
            this.selectedVerticalAlign = leadingSpanData.labelFormat.verticalAlign;
            if (this.verticalAlignBottomButton()) {
              this.verticalAlignBottomButton().nativeElement.classList.remove('ql-active');
            }
            if (this.verticalAlignTopButton()) {
              this.verticalAlignTopButton().nativeElement.classList.remove('ql-active');
            }
            if (this.verticalAlignMiddleButton()) {
              this.verticalAlignMiddleButton().nativeElement.classList.remove('ql-active');
            }
            if (this.selectedVerticalAlign === LabelFormat.VALIGN_BOTTOM && this.verticalAlignBottomButton) {
              this.verticalAlignBottomButton()?.nativeElement.classList.add('ql-active');
            } else if (this.selectedVerticalAlign === LabelFormat.VALIGN_TOP && this.verticalAlignTopButton) {
              this.verticalAlignTopButton()?.nativeElement.classList.add('ql-active');
            } else if (this.selectedVerticalAlign === LabelFormat.VALIGN_MIDDLE && this.verticalAlignMiddleButton) {
              this.verticalAlignMiddleButton()?.nativeElement.classList.add('ql-active');
            }
          }
        }
      }
    }
  }

  private updateMultilineSelect(): void {
    if (this.table) {
      const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), true);
      let commonMaxMultilineCount: number = -1;
      let commonCurrentMultilineCount: number = -1;
      for (const selectedCell of selectedCells) {
        const maxMultilineCount: number = this.tableProvider.getMaxMultilineCount(selectedCell[0], selectedCell[1]);
        if (!isNaN(maxMultilineCount)) {
          if (commonMaxMultilineCount === -1) {
            commonMaxMultilineCount = maxMultilineCount;
          } else {
            commonMaxMultilineCount = Math.min(commonMaxMultilineCount, maxMultilineCount);
          }
        }
        if (!isNaN(commonCurrentMultilineCount) && !isNaN(maxMultilineCount) && maxMultilineCount > 1) {
          const lineSpanInfo: LineSpanInfo = this.tableProvider.getLineSpanInfoForRow(selectedCell[0]);
          const spanData: ISpanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
          const currentMultilineCount: number = lineSpanInfo.lineSpan / spanData.rowSpan;
          if (commonCurrentMultilineCount === -1) {
            commonCurrentMultilineCount = currentMultilineCount;
          } else if (commonCurrentMultilineCount !== currentMultilineCount) {
            commonCurrentMultilineCount = NaN;
          }
        }
      }
      const lastSelectedCellRange = this.table.getSelectedLast();
      if (lastSelectedCellRange && lastSelectedCellRange.length >= 2) {
        const leadingSpanData: ISpanData = this.tableProvider.getSpanData(lastSelectedCellRange[0], lastSelectedCellRange[1]);
        const leadingLineSpanInfo: LineSpanInfo = this.tableProvider.getLineSpanInfoForRow(lastSelectedCellRange[0]);
        if (leadingSpanData !== null && leadingLineSpanInfo !== null) {
          let mergedEndCellFound: boolean = false;
          let r: number = leadingLineSpanInfo.startRow;
          rLoop: while (r < leadingLineSpanInfo.startRow + leadingLineSpanInfo.lineSpan) {
            const spanData: ISpanData = this.tableProvider.getSpanData(r, lastSelectedCellRange[1]);
            if (spanData.parent) {
              mergedEndCellFound = true;
              break rLoop;
            }
            r += spanData.rowSpan;
          }
          if (mergedEndCellFound) {
            this.currentMaxMultilineCount = 1;
            this.selectedMultilineCount = 1;
          } else {
            this.currentMaxMultilineCount = Math.max(1, commonMaxMultilineCount);
            this.selectedMultilineCount = isNaN(commonCurrentMultilineCount) ? -1 : commonCurrentMultilineCount;
          }
        } else {
          this.currentMaxMultilineCount = 1;
          this.selectedMultilineCount = 1;
        }
      } else {
        this.currentMaxMultilineCount = 1;
        this.selectedMultilineCount = 1;
      }
    } else {
      this.currentMaxMultilineCount = 1;
      this.selectedMultilineCount = 1;
    }
    if (this.multilineSelects()) {
      const newSelectedMultilineCount: number = this.selectedMultilineCount;
      // needs to be set here directly on native html element, so changes are visible for editor on update/reset
      // update of bindings to elements would happen later & be invisible for editor, which replaces select with custom elements
      this.multilineSelects().forEach(function (item: ElementRef) {
        item.nativeElement.value = newSelectedMultilineCount;
      });
    }
  }

  private isMergeDivideCellsAvailable(isMerge: boolean): boolean {
    const cellRange: CellRange = this.getMergeDivideTargetCellRange();
    if (cellRange === null) {
      return false;
    }
    let isMergePossible: boolean = true;
    let isDividePossible: boolean = false;
    let commonRowSpan: number = -1;
    const visistedSpanData: Set<ISpanData> = new Set<ISpanData>();
    for (let i: number = cellRange.from.col; i <= cellRange.to.col; i++) {
      const spanData: ISpanData = this.tableProvider.getSpanData(cellRange.from.row, i);
      if (!spanData) {
        isMergePossible = false;
      }
      if (spanData && spanData.parent) {
        if (!visistedSpanData.has(spanData.parent)) {
          isMergePossible = false;
        } else if (i < cellRange.to.col && !spanData.isMergeWithNext) {
          // for last column, it does not need to be checked, if it can be merged with next
          isMergePossible = false;
        }
      }
      if (spanData && !spanData.isEndPlate && !spanData.parent) {
        visistedSpanData.add(spanData);
        if (cellRange.from.row + spanData.rowSpan - 1 !== cellRange.to.row && cellRange.from.row !== cellRange.to.row) {
          return false;
        }
        if (commonRowSpan === -1) {
          commonRowSpan = spanData.rowSpan;
        } else if (commonRowSpan !== spanData.rowSpan) {
          return false;
        }
        if (i < cellRange.to.col && !spanData.isMergeWithNext) {
          // for last column, it does not need to be checked, if it can be merged with next
          isMergePossible = false;
        }
        if (i === cellRange.from.col && i + spanData.colSpan - 1 === cellRange.to.col) {
          // selected cell already spans complete selected range, there is nothing left to merge
          isMergePossible = false;
          // only if cell spans complete selected range, we allow splitting it
          isDividePossible = true;
        }
      }
    }
    return isMerge ? isMergePossible : isDividePossible;
  }

  private updateMergeDivideCopyClearCellsAvailability(): void {
    if (this.table) {
      this.isMergeCellsEnabled = this.isMergeDivideCellsAvailable(true);
      this.isDivideCellsEnabled = this.isMergeDivideCellsAvailable(false);
      // as delete key not available/effective for disabled cells, so should celar cells not be available
      const cellsSelected: boolean = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected()).length > 0;
      this.isClearCellsEnabled = cellsSelected;
      this.isCopyCellsEnabled = cellsSelected;
    } else {
      this.isMergeCellsEnabled = false;
      this.isDivideCellsEnabled = false;
      this.isClearCellsEnabled = false;
      this.isCopyCellsEnabled = false;
    }
  }

  private updateJoinLabelStripsAvailability(): void {
    if (this.table && !this.markingSystemLabelModeEnabled()) {
      this.isJoinLabelStripsEnabled = this.isJoinLabelStripsAvailable();
    } else {
      this.isJoinLabelStripsEnabled = false;
    }
  }

  private isJoinLabelStripsAvailable(): boolean {
    if (this.mode === LabelEditorMode.TEXT_CHECK_FAILED) {
      return false;
    }
    if (this.isUnusedLabelingSelected()) {
      return false;
    }
    return this.labelStripJoiner.isOperationAvailable(this.getSelectedLabelArticleContainers());
  }

  private isUnusedLabelingSelected(): boolean {
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), false);
    const selectedCellsSpanData: ISpanData[] = selectedCells.map((cell: number[]) => this.tableProvider.getSpanData(cell[0], cell[1]));
    return ArrayUtils.sourceArrayContainsElementFromTargetArray(selectedCellsSpanData, this.tableProvider.getUnusedLabelingSpanData());
  }

  private getSelectedLabelArticleContainers(): ArticleContainer[] {
    const articleContainers: ArticleContainer[] = [];
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), false);
    for (const selectedCell of selectedCells) {
      const spanData: ISpanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
      if (isConnectionSpanData(spanData)) {
        const articleContainer: ArticleContainer = spanData.connection.articleContainer;
        if (!articleContainers.includes(articleContainer)) {
          articleContainers.push(articleContainer);
        }
      }
    }
    return articleContainers;
  }

  public async joinLabelStrips(): Promise<void> {
    if (!this.isJoinLabelStripsEnabled) {
      return;
    }
    await this.labelStripJoiner.joinLabelStrips(this.getSelectedLabelArticleContainers());
  }

  private updateAssistentAvailability(): void {
    if (this.table) {
      this.isAssistantEnabled = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected()).length > 0;
    } else {
      this.isAssistantEnabled = false;
    }
  }

  private updateAddSymbolAvailability(): void {
    if (this.table) {
      this.isAddSymbolEnabled = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected()).length > 0;
    } else {
      this.isAddSymbolEnabled = false;
    }
  }

  private updateQuickMarking(): void {
    this.isQuickMarkingFromFocused = false;
    if (this.table) {
      const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), true);
      if (ArrayUtils.isNotEmpty(selectedCells)) {
        this.isQuickMarkingAvailable = true;
        this.quickMarkingTo = (selectedCells.length + parseInt(this.quickMarkingFrom, 10) - 1).toString();
        this.isPENOptionsAvailable = !this.markingSystemLabelModeEnabled() && !this.isSelectedCellsHaveMergedColumns();
      } else {
        this.isQuickMarkingAvailable = false;
        this.quickMarkingTo = 'n';
        this.isPENOptionsAvailable = false;
      }
    } else {
      this.isQuickMarkingAvailable = false;
      this.quickMarkingTo = 'n';
      this.isPENOptionsAvailable = false;
    }
  }

  private isSelectedCellsHaveMergedColumns(): boolean {
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), false);
    for (const selectedCell of selectedCells) {
      const spanData: ISpanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
      if (spanData.colSpan > 1) {
        return true;
      }
    }
    return false;
  }

  public onQuickMarkingFromChange(event): void {
    if (!event.target.value) {
      this.quickMarkingFrom = '0';
    }
    this.isQuickMarkingFromFocused = false;
  }

  private afterTableScroll(): void {
    if (this.isEditorOpen()) {
      this.scrolled$.next();
    }
  }

  private afterTableBeginEditing(row, column): void {
    this.currentlyEditedSpanData = this.tableProvider.getSpanData(row, column);
    this.currentlyEditedSpanDataCheckResults = null;
  }

  private afterTablePaste(data, coords): void {
    if (this.isCopyFromExcelActivated) {
      this.isPasteFromExcelDone = true;
    }
  }


  // this handler is used on capture phase
  public documentKeyDown(event: KeyboardEvent): void {
    const ctrlDown: boolean = (event.ctrlKey || event.metaKey) && !event.altKey;
    if (ctrlDown && (event.keyCode === 77 || event.which === 77)) {
      // CTRL+M is used by handsontable to merge any selected cells, circumventing any custom checks,
      // this needs to be avoided, we need to do it here in capture phase because mergePlugin registers its hook first with handsontable:
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (this.windowService.getModalsCount() > 0) {
      return;
    } else if (ctrlDown && (event.keyCode === 65 || event.which === 65)) {
      if (!this.isEditorOpen() && !this.isCopyFromExcelActivated) {
        // CTRL+A should be able to be used to select all cells, even in case table currently does not have focus:
        event.preventDefault();
        event.stopImmediatePropagation();
        this.selectAllCells();
      }
    } else if (event.keyCode === 9 || event.which === 9) {
      if (this.isEditorOpen()) {
        // tabs are not allowed to be used inside text (causes formatting issues in editor)
        event.preventDefault();
        event.stopImmediatePropagation();
        // trigger moving selection to next call in table (thus also closing currently open editor)
        this.table.selection.transformStart(0, 1);
        // we need to do this, as transforming the selection alone does not set proper input focuses
        const selectedCellRange = this.table.getSelectedLast();
        this.table.selectCell(selectedCellRange[0], selectedCellRange[1]);
      }
    }
  }

  public handleKeyDownEventOnLabelEditor(event: KeyboardEvent): void {
    if (this.isEditorOpen()) {
      const ctrlDown: boolean = (event.ctrlKey || event.metaKey) && !event.altKey;
      if (event.keyCode === 13 || event.which === 13) {
        // when using direct editing of cells, editor-manager of handsontable catches return keys
        // and wants to close editor, we want to be able to edit multiple lines of text, so we need to avoid that:
        event.stopImmediatePropagation();
      } else if (
        event.keyCode === 8 ||
        event.which === 8 ||
        event.keyCode === 46 ||
        event.which === 46 ||
        (event.keyCode >= 35 && event.keyCode <= 40) ||
        (event.which >= 35 && event.which <= 40)
      ) {
        // when using direct editing of cells, handsontable catches backspace, delete key and arrow keys, Home and End
        // and wants to delete cell content or navigate to different cells, we want to be able to edit text inside opened label editor as usual,
        // so we need to stop handsontable from doing that:
        event.stopImmediatePropagation();
      } else if (event.keyCode === 9 || event.which === 9) {
        // tabs are not allowed to be used inside text (causes formatting issues in editor)
        event.preventDefault();
        event.stopImmediatePropagation();
      } else if (ctrlDown && (event.keyCode === 77 || event.which === 77)) {
        // CTRL+M is used by handsontable to merge any selected cells, circumventing any custom checks,
        // this needs to be avoided:
        event.stopImmediatePropagation();
      }
    }
  }

  public handlePasteInLabelEditor(event: ClipboardEvent): void {
    if (!this.isPasteInLabelEditorAllowed || !this.isEditorOpen()) {
      event.preventDefault();
    }
  }

  public handleCopyInLabelEditor(event: ClipboardEvent): void {
    if (this.isEditorOpen()) {
      const range = this.editor.getSelection();
      if (range && range.length > 0) {
        // we only (try to) allow copy and paste inside opened editor
        // so we try to exclude copied content, which is coming for somewhere else
        // copy done in label editor, must not be empty, because otherwise such copy does not replace existing content inside clipboard
        this.isPasteInLabelEditorAllowed = true;
      }
    }
  }

  private editorSelectionChange(range, oldRange, source): void {
    if (range) {
      this.lastSelectedRangeInEditor = range;
    } else if (this.lastSelectedRangeInTable && !this.isLabelEditorAssistantOpen && !this.isQuickMarkingFromFocused) {
      // editor (might have) lost focus, this also means table should have lost focus and selection
      // but handsontable does not seem to throw event for selection change in such case, thus:
      this.afterTableSelectionEnd();
    }
    this.cdRef.markForCheck();
  }

  private editorTextChange(delta, oldContents, source): void {
    if (source !== 'user' || !this.isEditorOpen()) return;

    const currentText = this.editor.getText();
    const range = this.editor.getSelection();
    const line = range === null ? null : this.editor.getLine(range.index);
    const blot = line ? line[0] : null;
    const currentLineText: string = blot === null || blot.domNode === null ? null : blot.domNode.innerText;


        let format = this.getTextChangedFormat();

    if (currentText === null || currentText === '' || currentText === '\n') {
      this.editor.insertText(0, '\uFEFF', 'api');
      this.editor.setSelection(0, 0, 'api');
    } else if (range && (currentLineText === null || currentLineText === '' || currentLineText === '\n')) {
      this.editor.insertText(range.index, '\uFEFF', 'api');
      this.editor.setSelection(range.index, 0, 'api');
    }

    this.editor.formatText(0, this.editor.getText().length - 1, format, 'api');

    // validation needs to be done to update contains errors in case complete text gets deleted
    this.performValidationOnCurrentTextInEditor();
  }

  private getTextChangedFormat() {
    // when entering a new line, we need to ensure that formatting is preserved for the complete text (taking the format from the first character).
    // At the same time for example changing the text size should stil work when mid-text, so we need to respect the format of the current character too
    const formatAtIndex0 = this.editor.getFormat(0);

    let format = this.editor.getFormat();

    format['color'] ??= "#000000";

    for (const key in formatAtIndex0) {
      if (formatAtIndex0[key] !== undefined && format[key] === undefined) {
        format[key] = formatAtIndex0[key];
      }
    }

    // when there is no text to get the format from, we need to recreate it
    if (format['size'] == undefined) {
      format = {
        bold: this.currentlyEditedSpanData.defaultTextParameters.bold,
        size: this.currentlyEditedSpanData.defaultTextParameters.html5DataFontSize + 'px',
      };
    }
    return format;
  }

  private performValidationOnCurrentTextInEditor(): void {
    this.currentlyEditedSpanDataCheckResults = ConstraintCheckUtils.performValidationOnCurrentTextInEditor(
      this.useStrictTextCheckConformity(),
      this.localeResourceManager,
      this.editor,
      this.currentlyEditedSpanData
    );
    this.updateTooltip(this.editor['container']);
  }

  public verticalAlignBottom(): void {
    this.selectedVerticalAlign = LabelFormat.VALIGN_BOTTOM;
    this.applyCustomFormatSettings(false, true, false, false);
  }

  public verticalAlignTop(): void {
    this.selectedVerticalAlign = LabelFormat.VALIGN_TOP;
    this.applyCustomFormatSettings(false, true, false, false);
  }

  public verticalAlignMiddle(): void {
    this.selectedVerticalAlign = LabelFormat.VALIGN_MIDDLE;
    this.applyCustomFormatSettings(false, true, false, false);
  }

  public textRotationUpdate(): void {
    this.selectedTextRotation = this.textRotationOptions[this.textRotationSelect()?.nativeElement.selectedIndex]['textRotation'];
    this.applyCustomFormatSettings(true, false, false, false);
  }

  public rotateTextByStep(): void {
    const currentIndex: number = this.textRotationSelect()?.nativeElement.selectedIndex;
    this.selectedTextRotation =
      currentIndex === this.textRotationOptions.length - 1
        ? this.textRotationOptions[0]['textRotation']
        : this.textRotationOptions[currentIndex + 1]['textRotation'];
    this.applyCustomFormatSettings(true, false, false, false);
  }

  public textCompressionUpdate(): void {
    this.selectedTextCompression = this.textCompressionOptions[this.textCompressionSelect()?.nativeElement.selectedIndex]['textCompression'];
    this.applyCustomFormatSettings(false, false, true, false);
  }

  public async autoFormatByTextCheckUpdate(): Promise<void> {
    const newTextCheckSelection = this.autoFormatByTextCheckCheckBox()?.nativeElement.checked;
    if (!newTextCheckSelection && !await this.confirmTextCheckDeactivation()) {
      // change denied by user, revert checkbox to selected state.
      this.autoFormatByTextCheckCheckBox().nativeElement.checked = true;
      return;
    }
    this.isAutoFormatByTextCheckSelected = newTextCheckSelection;
    this.applyCustomFormatSettings(false, false, false, true);
  }

  private async confirmTextCheckDeactivation(): Promise<boolean> {
    const SESSION_STORAGE_KEY = 'TextCheckAlertShown';

    if (SessionStorageUtils.getBoolean(SESSION_STORAGE_KEY)) {
      // User has already confirmed once, confirm without alert.
      return true;
    }

    const buttonResult = await this.alertHelper.showAlert('labelEditorPopup.alert.title.textcheck_deactivated',
      'labelEditorPopup.alert.message.textcheck_deactivated', null, [AlertButton.YES, AlertButton.NO], "exclamation");

    if (buttonResult === AlertButton.YES) {
      // Save user confirmation to session storage.
      SessionStorageUtils.setBoolean(SESSION_STORAGE_KEY, true);
      return true;
    }
    return false;
  }

  public multilineUpdate(newMultilineCountString: string): void {
    const newMultilineCount: number = parseInt(newMultilineCountString, 10);
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), true, true);
    const changeMultilineAction: ChangeMultilineAction = this.changeMultilineCount(selectedCells, newMultilineCount);
    if (changeMultilineAction !== null) {
      this.table.undoRedo.done(changeMultilineAction);
    }
    this.selectedMultilineCount = newMultilineCount;
    // needs to be set here directly on native html element, so changes are visible for editor on update/reset
    // update of bindings to elements would happen later & be invisible for editor, which replaces select with custom elements
    this.multilineSelects()?.forEach(function (item: ElementRef) {
      item.nativeElement.value = newMultilineCount;
    });
  }

  private disableTableRendering(): void {
    this.tableRenderingState.isEnabled = false;
  }

  private enableTableRendering(): void {
    this.tableRenderingState.isEnabled = true;
  }

  public changeMultilineCount(
    cells: number[][],
    newMultilineCount: number,
    restoreCellCoordsStringToOldColSpanMap: Map<string, number> = null
  ): ChangeMultilineAction {
    if (cells == null || cells.length === 0 || isNaN(newMultilineCount) || newMultilineCount <= 0) {
      return null;
    }
    // assumes that merged cells across columns (colSpan > 1) have the same maxMultilineCount
    const mergeCellsPlugin: any = this.table.getPlugin('mergeCells');
    const changedCells: number[][] = [];
    const oldMultilineCounts: number[] = [];
    const oldCellDataList: [number, number, string][] = [];
    const newCellDataList: [number, number, string][] = [];
    const cellCoordsStringToOldColSpanMap: Map<string, number> = new Map<string, number>();
    this.disableTableRendering();
    let executeRender: boolean = true; // as we now disable rendering by handsontable, we must render in any case at the end
    cellLoop: for (const cell of cells) {
      const row: number = cell[0];
      const col: number = cell[1];
      const lineSpanInfo: LineSpanInfo = this.tableProvider.getLineSpanInfoForRow(row);
      if (lineSpanInfo && lineSpanInfo.lineSpan > 1) {
        const spanData: ISpanData = this.tableProvider.getSpanData(lineSpanInfo.startRow, col);

        const currentMultilineCount: number = lineSpanInfo.lineSpan / spanData.rowSpan;
        if (
          spanData &&
          !spanData.parent &&
          (newMultilineCount <= spanData.maxMultilineCount || spanData.isEndPlate) &&
          currentMultilineCount !== newMultilineCount
        ) {
          let maxColSpan: number;
          let allColSpansAreEqual: boolean = true;
          if (restoreCellCoordsStringToOldColSpanMap) {
            // it is undo operation, so we need to look at restore data:
            maxColSpan = restoreCellCoordsStringToOldColSpanMap.get('(' + lineSpanInfo.startRow + ',' + col + ')');
            let s: number = lineSpanInfo.startRow + 1;
            while (s < lineSpanInfo.startRow + lineSpanInfo.lineSpan) {
              const sDataRestoreColSpan: number = restoreCellCoordsStringToOldColSpanMap.get('(' + s + ',' + col + ')');
              if (sDataRestoreColSpan && !isNaN(sDataRestoreColSpan)) {
                if (sDataRestoreColSpan !== maxColSpan) {
                  allColSpansAreEqual = false;
                  maxColSpan = Math.max(maxColSpan, sDataRestoreColSpan);
                }
              }
              s++;
            }
          } else {
            maxColSpan = spanData.colSpan;
            cellCoordsStringToOldColSpanMap.set('(' + lineSpanInfo.startRow + ',' + col + ')', spanData.colSpan);
            let s: number = lineSpanInfo.startRow + spanData.rowSpan;
            while (s < lineSpanInfo.startRow + lineSpanInfo.lineSpan) {
              const sData: ISpanData = this.tableProvider.getSpanData(s, col);
              if (sData.parent) {
                continue cellLoop;
              } else {
                if (sData.colSpan !== maxColSpan) {
                  allColSpansAreEqual = false;
                  maxColSpan = Math.max(maxColSpan, sData.colSpan);
                }
                cellCoordsStringToOldColSpanMap.set('(' + s + ',' + col + ')', sData.colSpan);
              }
              s += sData.rowSpan;
            }
          }
          changedCells.push(cell);
          oldMultilineCounts.push(currentMultilineCount);
          let start: CellCoords = new CellCoords(lineSpanInfo.startRow, col);
          let end: CellCoords = new CellCoords(lineSpanInfo.startRow + lineSpanInfo.lineSpan - 1, col);
          // backup data
          // TODO: maybe backup/restore data could be done differently to improve performance
          const data = this.table.getData(start.row, start.col, end.row, end.col);
          if (data) {
            const oldCellData: [number, number, string][] = [];
            for (let r: number = 0; r < data.length; r++) {
              const rowData: string[] = data[r];
              for (let c: number = 0; c < rowData.length; c++) {
                oldCellData.push([start.row + r, start.col + c, rowData[c]]);
              }
            }
            oldCellDataList.push(...oldCellData);
            if (!restoreCellCoordsStringToOldColSpanMap) {
              this.setChangeMultilineNewCellDataList(lineSpanInfo.lineSpan, currentMultilineCount, newMultilineCount, oldCellData, newCellDataList);
            }
          }
          if (allColSpansAreEqual || !restoreCellCoordsStringToOldColSpanMap) {
            if (allColSpansAreEqual) {
              end = new CellCoords(lineSpanInfo.startRow + lineSpanInfo.lineSpan - 1, col + (allColSpansAreEqual ? maxColSpan - 1 : 0));
              const unmergeCellRange: CellRange = new CellRange(start, start, end);
              mergeCellsPlugin.unmergeRange(unmergeCellRange, true);
            } else {
              let r: number = lineSpanInfo.startRow;
              while (r < lineSpanInfo.startRow + lineSpanInfo.lineSpan) {
                const sData: ISpanData = this.tableProvider.getSpanData(r, col);
                const rowSpan: number = sData.rowSpan; // need to remember rowSpan here, following unmerge triggers reset to 1
                const colSpan: number = sData.colSpan; // need to remember colSpan here, following unmerge triggers reset to 1
                start = new CellCoords(r, col);
                end = new CellCoords(r + rowSpan - 1, col + colSpan - 1);
                const unmergeCellRange: CellRange = new CellRange(start, start, end);
                mergeCellsPlugin.unmergeRange(unmergeCellRange, true);
                let c: number = col + 1;
                while (c < col + colSpan) {
                  start = new CellCoords(r, c);
                  end = new CellCoords(r + rowSpan - 1, c);
                  const mergeCellRange: CellRange = new CellRange(start, start, end);
                  mergeCellsPlugin.mergeRange(mergeCellRange, true);
                  c += 1;
                }
                r += rowSpan;
              }
            }
            for (let i: number = 0; i < newMultilineCount; i++) {
              start = new CellCoords(lineSpanInfo.startRow + i * (lineSpanInfo.lineSpan / newMultilineCount), col);
              end = new CellCoords(
                lineSpanInfo.startRow + (i + 1) * (lineSpanInfo.lineSpan / newMultilineCount) - 1,
                col + (allColSpansAreEqual ? maxColSpan - 1 : 0)
              );
              const mergeCellRange: CellRange = new CellRange(start, start, end);
              mergeCellsPlugin.mergeRange(mergeCellRange, true);
            }
          } else {
            // undo operation with different colspans:
            start = new CellCoords(lineSpanInfo.startRow, col);
            end = new CellCoords(lineSpanInfo.startRow + lineSpanInfo.lineSpan - 1, col);
            let unmergeCellRange: CellRange = new CellRange(start, start, end);
            mergeCellsPlugin.unmergeRange(unmergeCellRange, true);
            let r: number = lineSpanInfo.startRow;
            const restoreRowSpan: number = lineSpanInfo.lineSpan / newMultilineCount;
            while (r < lineSpanInfo.startRow + lineSpanInfo.lineSpan) {
              let c: number = col + 1;
              const restoreColSpan: number = restoreCellCoordsStringToOldColSpanMap.get('(' + r + ',' + col + ')');
              // TODO: maybe possible to use single unmerge call here
              while (c < col + restoreColSpan) {
                start = new CellCoords(r, c);
                end = new CellCoords(r + restoreRowSpan - 1, c);
                unmergeCellRange = new CellRange(start, start, end);
                mergeCellsPlugin.unmergeRange(unmergeCellRange, true);
                c++;
              }
              r += restoreRowSpan;
            }
            for (let i: number = 0; i < newMultilineCount; i++) {
              const startRow: number = lineSpanInfo.startRow + i * (lineSpanInfo.lineSpan / newMultilineCount);
              const restoreColSpan: number = restoreCellCoordsStringToOldColSpanMap.get('(' + startRow + ',' + col + ')');
              start = new CellCoords(startRow, col);
              end = new CellCoords(lineSpanInfo.startRow + (i + 1) * (lineSpanInfo.lineSpan / newMultilineCount) - 1, col + restoreColSpan - 1);
              const mergeCellRange: CellRange = new CellRange(start, start, end);
              mergeCellsPlugin.mergeRange(mergeCellRange, true);
            }
          }
          // in case end plate is present, it might be not really visually selected (as exploded range could be expanded).
          // handsontable will not rerender that part of table (end plate) automatically
          // so we must trigger render ourselves at the end in such case
          if (spanData.isEndPlate) {
            executeRender = true;
          }
        }
      } else {
        oldMultilineCounts.push(NaN);
      }
    }
    if (changedCells === null || changedCells.length === 0) {
      this.enableTableRendering();
      return null;
    }
    if (!restoreCellCoordsStringToOldColSpanMap && ArrayUtils.isNotEmpty(newCellDataList)) {
      // using MergeCells here as source identifier, so that no undo actions are pushed to Undo-Redo-Stack by handsontable:
      this.table.setDataAtCell(newCellDataList, undefined, undefined, 'MergeCells');
    }
    this.updateAllTextCheckDirtySpanData(!restoreCellCoordsStringToOldColSpanMap); // restoreCellCoordsStringToOldColSpanMap === it is undo operation
    // need to make sure, that visible (non merged cell gets selected, after multiline was changed)
    // otherwise handsontable keeps hidden/merged cells internally selected, while visually a different cell appears to be selected
    const lastSelectedCell: number[] = cells[0];
    const selectedLineSpanInfo: LineSpanInfo = this.tableProvider.getLineSpanInfoForRow(lastSelectedCell[0]);
    if (selectedLineSpanInfo) {
      this.table.selectCell(selectedLineSpanInfo.startRow, lastSelectedCell[1]);
    } else {
      this.table.selectCell(lastSelectedCell[0], lastSelectedCell[1]);
    }
    this.enableTableRendering();
    if (executeRender) {
      this.table.render();
    }
    return new ChangeMultilineAction(changedCells, oldMultilineCounts, newMultilineCount, oldCellDataList, cellCoordsStringToOldColSpanMap);
  }

  private setChangeMultilineNewCellDataList(
    lineSpan: number,
    currentMultilineCount: number,
    newMultilineCount: number,
    oldCellData: [number, number, string][],
    newCellDataList: [number, number, string][]
  ): void {
    const newCellData: [number, number, string][] = oldCellData.map((data: [number, number, string]) => [data[0], data[1], null]);
    const newIndexOldIndexMap: Map<number, number> = this.getChangeMultilineNewIndexOldIndexMap(lineSpan, currentMultilineCount, newMultilineCount);
    for (let i = 0; i < newCellData.length; i++) {
      if (newIndexOldIndexMap.has(i)) {
        const oldData: [number, number, string] = oldCellData[newIndexOldIndexMap.get(i)];
        if (oldData) {
          newCellData[i][2] = oldData[2];
        }
      }
    }
    newCellDataList.push(...newCellData);
  }

  private getChangeMultilineNewIndexOldIndexMap(lineSpan: number, currentMultilineCount: number, newMultilineCount: number): Map<number, number> {
    const newIndexOldIndexMap = new Map<number, number>();
    const currentParentIndexes: number[] = LabelEditorUtils.getParentRowIndexes(lineSpan, currentMultilineCount);
    const newParentIndexes: number[] = LabelEditorUtils.getParentRowIndexes(lineSpan, newMultilineCount);
    const multilineCountDifference: number = Math.abs(currentMultilineCount - newMultilineCount);
    if (ArrayUtils.isNotEmpty(currentParentIndexes) && ArrayUtils.isNotEmpty(newParentIndexes)) {
      for (let i = 0; i < newParentIndexes.length; i++) {
        if (newMultilineCount > currentMultilineCount) {
          if (i >= multilineCountDifference) {
            newIndexOldIndexMap.set(newParentIndexes[i], currentParentIndexes[i - multilineCountDifference]);
          }
        } else {
          newIndexOldIndexMap.set(newParentIndexes[i], currentParentIndexes[i + multilineCountDifference]);
        }
      }
    }
    return newIndexOldIndexMap;
  }

  public mergeAndDivideCells(
    cellRange: CellRange,
    isMerge: boolean,
    restoreCellCoordsStringToOldColSpanMap: Map<string, number> = null
  ): MergeAndDivideAction {
    if (cellRange === null) {
      return null;
    }
    this.disableTableRendering();
    // normalize cell range:
    const start: CellCoords = new CellCoords(Math.min(cellRange.from.row, cellRange.to.row), Math.min(cellRange.from.col, cellRange.to.col));
    const end: CellCoords = new CellCoords(Math.max(cellRange.from.row, cellRange.to.row), Math.max(cellRange.from.col, cellRange.to.col));
    cellRange = new CellRange(start, start, end);
    const spanData: ISpanData = this.tableProvider.getSpanData(cellRange.from.row, cellRange.from.col);
    if (spanData === null) {
      return null;
    }
    const mergeCellsPlugin: any = this.table.getPlugin('mergeCells');
    const oldCellDataList = [];
    const cellCoordsStringToOldColSpanMap: Map<string, number> = new Map<string, number>();
    const startMerge: CellCoords = new CellCoords(cellRange.from.row, cellRange.from.col);
    // merged across rows, which are not multilined are not possible, thus we have:
    const endMerge: CellCoords = new CellCoords(cellRange.from.row + spanData.rowSpan - 1, cellRange.to.col);
    const mergeCellRange: CellRange = new CellRange(startMerge, startMerge, endMerge);

    if (!restoreCellCoordsStringToOldColSpanMap) {
      // no undo operation, so we collect old col span data:
      let c: number = mergeCellRange.from.col;
      while (c <= mergeCellRange.to.col) {
        const sData: ISpanData = this.tableProvider.getSpanData(mergeCellRange.from.row, c);
        if (!sData.parent && sData.colSpan > 1) {
          cellCoordsStringToOldColSpanMap.set('(' + mergeCellRange.from.row + ',' + c + ')', sData.colSpan);
        }
        c += sData.colSpan;
      }
    }

    // backup data
    // TODO: maybe backup/restore data could be done differently to improve performance
    const data = this.table.getData(cellRange.from.row, cellRange.from.col, cellRange.to.row, cellRange.to.col);
    if (data) {
      for (let i: number = 0; i < data.length; i++) {
        const rowData = data[i];
        for (let j: number = 0; j < rowData.length; j++) {
          const oldCellData = [cellRange.from.row + i, cellRange.from.col + j, rowData[j]];
          oldCellDataList.push(oldCellData);
        }
      }
    }

    // at first we need to unmerge any existing merged cells because of existing multilines or other previous merges:
    mergeCellsPlugin.unmergeRange(mergeCellRange, true);
    if (isMerge) {
      mergeCellsPlugin.mergeRange(mergeCellRange, true);
    } else {
      // we need to restore merges for existing multilines and merges for old colSpans in each column:
      if (mergeCellRange.to.row > mergeCellRange.from.row) {
        let c: number = mergeCellRange.from.col;
        while (c <= mergeCellRange.to.col) {
          let colSpan: number = 1;
          if (restoreCellCoordsStringToOldColSpanMap) {
            colSpan = restoreCellCoordsStringToOldColSpanMap.get('(' + mergeCellRange.from.row + ',' + c + ')');
            if (!colSpan || isNaN(colSpan)) {
              colSpan = 1;
            }
          }
          const startRemerge: CellCoords = new CellCoords(mergeCellRange.from.row, c);
          const endRemerge: CellCoords = new CellCoords(mergeCellRange.to.row, c + colSpan - 1);
          const remergeCellRange: CellRange = new CellRange(startRemerge, startRemerge, endRemerge);
          mergeCellsPlugin.mergeRange(remergeCellRange, true);
          c += colSpan;
        }
      }
    }
    this.updateAllTextCheckDirtySpanData(!restoreCellCoordsStringToOldColSpanMap); // restoreCellCoordsStringToOldColSpanMap === it is undo operation
    // need to make sure, that visible (non merged cell gets selected, after merge and divide)
    // otherwise handsontable keeps hidden/merged cells internally selected, while visually a different cell appears to be selected
    this.table.selectCell(cellRange.from.row, cellRange.from.col);
    this.enableTableRendering();
    this.table.render();
    return new MergeAndDivideAction(cellRange, isMerge, oldCellDataList, cellCoordsStringToOldColSpanMap);
  }

  private applyCustomFormatSettings(
    applyTextRotation: boolean,
    applyVerticalAlign: boolean,
    applyTextCompression: boolean,
    applyAutoFormatByTextCheck: boolean
  ): void {
    if (this.isEditorOpen()) {
      if (applyTextRotation) {
        this.currentlyEditedSpanData.labelFormat.direction = this.selectedTextRotation;
      }
      if (applyVerticalAlign) {
        this.currentlyEditedSpanData.labelFormat.verticalAlign = this.selectedVerticalAlign;
      }
      if (applyTextCompression) {
        this.currentlyEditedSpanData.compression = this.selectedTextCompression;
      }
      if (applyAutoFormatByTextCheck) {
        this.currentlyEditedSpanData.autoFormatByTextCheck = this.isAutoFormatByTextCheckSelected;
      }
      this.updateCustomFormatButtons();
      this.performValidationOnCurrentTextInEditor();
    } else {
      const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), false);
      const cellDataList = [];
      for (const selectedCell of selectedCells) {
        const spanData: ISpanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
        let data: string = this.table.getDataAtCell(selectedCell[0], selectedCell[1]);
        const isAutoFormatByTextCheck: boolean = applyAutoFormatByTextCheck ? this.isAutoFormatByTextCheckSelected : spanData.autoFormatByTextCheck;
        const checkResults: ConstraintCheckResults = ConstraintCheckUtils.performValidationOnSpanData(
          this.useStrictTextCheckConformity(),
          this.localeResourceManager,
          spanData,
          applyTextRotation ? this.selectedTextRotation : -1,
          applyTextCompression ? this.selectedTextCompression : -1,
          isAutoFormatByTextCheck
        );
        data = LabelEditorUtils.applyCustomFormatSettingsToHTML5Data(
          data,
          isAutoFormatByTextCheck ? checkResults.fontSize : NaN,
          applyTextRotation ? this.selectedTextRotation : -1,
          applyVerticalAlign ? this.selectedVerticalAlign : null,
          isAutoFormatByTextCheck && !isNaN(checkResults.textCompression)
            ? checkResults.textCompression
            : applyTextCompression
            ? this.selectedTextCompression
            : -1,
          applyAutoFormatByTextCheck ? this.isAutoFormatByTextCheckSelected : null,
          checkResults.containsErrors,
          checkResults.containsStrictConformityErrors,
          checkResults.errorTooltipText,
          spanData.font,
        );
        const cellData = [selectedCell[0], selectedCell[1], data];
        cellDataList.push(cellData);
      }
      if (cellDataList.length > 0) {
        this.table.setDataAtCell(cellDataList);
      }
    }
  }

  private updateAllTextCheckDirtySpanData(performValidationOnSpanData: boolean): void {
    const cellDataList = [];
    const rowCount: number = this.tableProvider.rowCount;
    const colCount: number = this.tableProvider.colCount;
    for (let row: number = 0; row < rowCount; row++) {
      for (let col: number = 0; col < colCount; col++) {
        const spanData: ISpanData = this.tableProvider.getSpanData(row, col);
        if (spanData && spanData.parent === null && spanData.enabled && !spanData.isEndPlate && spanData.isTextCheckDirty) {
          if (spanData.colSpan === 1) {
            spanData.cellLabelSizeInfos = this.textSizeCheckManager.getTextSizeCheckWithFallback(
              spanData.textCheckClass,
              spanData.spacingData,
              ArticleUtils.isDynamicLabelingArticle(spanData.article)
            );
          } else {
            let previousSpanData: ISpanData = spanData;
            let mergedSpacing: number = spanData.spacing;
            let cellCount: number = 1;
            for (let i: number = 1; i < spanData.colSpan; i++) {
              const mergedSpanData: ISpanData = this.tableProvider.getSpanData(row, col + i);
              if (mergedSpanData && !mergedSpanData.isEndPlate) {
                if (!isNaN(previousSpanData.spacingToNextSpanDataInRow)) {
                  mergedSpacing += previousSpanData.spacingToNextSpanDataInRow;
                }
                if (!isNaN(mergedSpanData.spacing)) {
                  mergedSpacing += mergedSpanData.spacing;
                }
                cellCount++;
                previousSpanData = mergedSpanData;
              }
            }
            spanData.cellLabelSizeInfos = this.textSizeCheckManager.getTextSizeCheckWithDecidingSpacing(
              spanData.textCheckClass,
              mergedSpacing / cellCount
            );
          }
          if (performValidationOnSpanData) {
            let data: string = spanData.html5data;
            const checkResults: ConstraintCheckResults = ConstraintCheckUtils.performValidationOnSpanData(
              this.useStrictTextCheckConformity(),
              this.localeResourceManager,
              spanData
            );
            data = LabelEditorUtils.applyCheckResultsToHTML5Data(data, spanData, checkResults);
            const cellData = [row, col, data];
            cellDataList.push(cellData);
          }
          spanData.isTextCheckDirty = false;
        }
      }
    }
    if (cellDataList.length > 0) {
      // using MergeCells here as source identifier, so that no undo actions are pushed to Undo-Redo-Stack by handsontable:
      this.table.setDataAtCell(cellDataList, undefined, undefined, 'MergeCells');
    }
  }

  public labelEditorFormatUpdate(changedEditorFormatName: "color" | "align" | "size"): void {
    if (!this.isEditorOpen()) {
      // if format for leading selected cell is not changed, editor will not fire text change event,
      // but still we must update format of all other selected cells
      this.isEditorFormatUpdateInProgress = true;
      this.changedEditorFormatName = changedEditorFormatName;
      setTimeout(this.delayedEditorFormatUpdateHandler, 100);
    }
  }

  private delayedEditorFormatUpdate(): void {
    if (this.isEditorFormatUpdateInProgress) {
      this.executeEditorFormatUpdate();
    }
  }

  private executeEditorFormatUpdate(): void {
    if (this.isEditorOpen()) {
      return;
    }

    const newFormat: Record<string, unknown> = this.editor.getFormat();
    const changedFormatValue = newFormat.hasOwnProperty(this.changedEditorFormatName) ? newFormat[this.changedEditorFormatName] : null;
    // we need to include disabled cells here, so that format button updates work correctly in case lead (last) selected cell is disabled
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected(), false);
    const cellDataList = [];
    const qlEditor = this.labelEditor()?.nativeElement.querySelector('.ql-editor');
    for (const selectedCell of selectedCells) {
      const data = this.table.getDataAtCell(selectedCell[0], selectedCell[1]);
      const spanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
      // TODO: could be changed to use custom processing (similar to addSymbol()) to increase performance
      qlEditor.innerHTML = data;
      this.editor.update('api');
      // range must be specified explicitly here in getFormat-call as otherwise only in IE11 it will not work:
      const currentFormat: Record<string, unknown> = this.editor.getFormat(0, this.editor.getLength());
      if (currentFormat) {
        if (changedFormatValue === null) {
          delete currentFormat[this.changedEditorFormatName];
        } else {
          currentFormat[this.changedEditorFormatName] = changedFormatValue;
        }
        this.editor.removeFormat(0, this.editor.getLength() - 1, 'api');
        this.editor.formatText(0, this.editor.getLength(), currentFormat, 'api');
      }
      this.editor.update('api');
      let newData: string = qlEditor.innerHTML;
      if (this.changedEditorFormatName === 'size') {
        newData = this.createNewData(spanData, newData);
      }
      const cellData = [selectedCell[0], selectedCell[1], newData];
      cellDataList.push(cellData);
    }
    if (cellDataList.length > 0) {
      this.table.setDataAtCell(cellDataList);
    }
    this.isEditorFormatUpdateInProgress = false;
    this.changedEditorFormatName = null;
  }

  private createNewData(spanData: ISpanData, data: string) {
    // only in case bold formatting or font size is changed, text checking could be effected
    const checkResults: ConstraintCheckResults = ConstraintCheckUtils.performValidationOnSpanData(
      this.useStrictTextCheckConformity(),
      this.localeResourceManager,
      spanData,
      -1,
      NaN,
      null,
      data
    );
    return LabelEditorUtils.applyCustomFormatSettingsToHTML5Data(
      data,
      LabelEditorUtils.getEffectiveFontSize(spanData, checkResults),
      -1,
      null,
      spanData.autoFormatByTextCheck && !isNaN(checkResults.textCompression) ? checkResults.textCompression : -1,
      null,
      checkResults.containsErrors,
      checkResults.containsStrictConformityErrors,
      checkResults.errorTooltipText,
      spanData.font,
    );
  }

  private isEditorOpen(): boolean {
    return this.labelEditor && this.labelEditor()?.nativeElement && this.labelEditor()?.nativeElement.style.display !== 'none';
  }

  private resetEditor(): void {
    if (this.editor) {
      if (!this.isEditorOpen()) {
        const selectedCellRange = this.table ? this.table.getSelectedLast() : null;
        const textRotationLabel = this.textRotationSpan ? this.textRotationSpan()?.nativeElement.querySelector('.ql-picker-label') : null;
        const textCompressionLabel = this.textCompressionSpan ? this.textCompressionSpan()?.nativeElement.querySelector('.ql-picker-label') : null;
        let data = null;
        if (selectedCellRange && selectedCellRange.length >= 2) {
          // we continue to use data from disabled cells so formatting works in case lead (last) selected cell is disabled
          data = this.table.getDataAtCell(selectedCellRange[0], selectedCellRange[1]);
        }
        if (data !== null) {
          if (this.labelEditor()) {
            this.labelEditor().nativeElement.querySelector('.ql-editor').innerHTML = data;
          }
          this.editor.update('api');
          const currentText = this.editor.getText();
          if (currentText === null || currentText === '' || currentText === '\n') {
            // should now never be the case anymore, be aware that this might clear any existing formatting for empty text,
            // and quill editor could no longer for example hightlight bold setting:
            this.editor.setText('data', 'api');
          }
          this.editor.setSelection(0, this.editor.getLength());
        } else {
          // to capture format changes while editor is hidden and there is no data at all determined (data === null):
          this.editor.setText('data', 'api');
          this.editor.setSelection(0, this.editor.getLength());
          if (this.verticalAlignBottomButton()) {
            this.verticalAlignBottomButton().nativeElement.classList.remove('ql-active');
          }
          if (this.verticalAlignTopButton()) {
            this.verticalAlignTopButton().nativeElement.classList.remove('ql-active');
          }
          if (this.verticalAlignMiddleButton()) {
            this.verticalAlignMiddleButton().nativeElement.classList.remove('ql-active');
          }
          if (this.horizontalAlignCenterButton()) {
            this.horizontalAlignCenterButton().nativeElement.classList.remove('ql-active');
          }
          if (this.horizontalAlignLeftButton()) {
            this.horizontalAlignLeftButton().nativeElement.classList.remove('ql-active');
          }
          if (this.horizontalAlignRightButton()) {
            this.horizontalAlignRightButton().nativeElement.classList.remove('ql-active');
          }
          if (textRotationLabel) {
            textRotationLabel.classList.remove('ql-active');
          }
          if (textCompressionLabel) {
            textCompressionLabel.classList.remove('ql-active');
          }
        }

        if (this.textRotationOptions === LabelEditorComponent.UNSELECTED_TEXT_ROTATION_OPTIONS) {
          if (textRotationLabel) {
            textRotationLabel.setAttribute('data-label', '-°');
          }
        }
        if (this.textCompressionOptions === LabelEditorComponent.UNSELECTED_TEXT_COMPRESSION_OPTIONS) {
          if (textCompressionLabel) {
            textCompressionLabel.setAttribute('data-label', '-%');
          }
        }
      }
    }
    // we never want to see active highlight for symbol selection label
    this.resetSymbolSelectionActiveState(this.symbolSelectionSpan());
    this.resetSymbolSelectionActiveState(this.symbolPESelectionSpan());
    this.resetSymbolSelectionActiveState(this.symbolNSelectionSpan());
  }

  private resetSymbolSelectionActiveState(symbolSelectionSpan: ElementRef): void {
    const symbolLabel = symbolSelectionSpan ? symbolSelectionSpan.nativeElement.querySelector('.ql-picker-label') : null;
    if (symbolLabel) {
      symbolLabel.classList.remove('ql-active');
    }
  }

  private refocusEditor(): void {
    const range = this.editor.getSelection(true); // also focuses editor if necessary
    if (range) {
      this.editor.setSelection(range.index, range.length); // makes cursor reappear again
    } else if (this.lastSelectedRangeInEditor) {
      // need to work with stored selected range because of problems with getting selection from quill in merged cells
      this.editor.setSelection(this.lastSelectedRangeInEditor.index, this.lastSelectedRangeInEditor.length);
    } else {
      this.editor.setSelection(this.editor.getLength(), 0);
    }
  }

  private getMergeDivideTargetCellRange(): CellRange {
    // If CTRL key is used to construct selection, then these are stored as separate selected ranges by handsontable.
    // We need to combine such separate ranges to allow merging/dividing cells for such cases.
    // Also we must exclude end plates (only if they are at the beginning or end of the target range).
    const selectedRanges = this.table.getSelected();
    if (!selectedRanges || selectedRanges === null || selectedRanges.length === 0) {
      return null;
    }
    const firstSelectedRange = selectedRanges[0];
    const start: CellCoords = new CellCoords(
      Math.min(firstSelectedRange[0], firstSelectedRange[2]),
      Math.min(firstSelectedRange[1], firstSelectedRange[3])
    );
    const end: CellCoords = new CellCoords(
      Math.max(firstSelectedRange[0], firstSelectedRange[2]),
      Math.max(firstSelectedRange[1], firstSelectedRange[3])
    );
    const result: CellRange = new CellRange(start, start, end);
    for (let i: number = 1; i < selectedRanges.length; i++) {
      const selectedRange = selectedRanges[i];
      const startRow: number = Math.min(selectedRange[0], selectedRange[2]);
      const endRow: number = Math.max(selectedRange[0], selectedRange[2]);
      const startCol: number = Math.min(selectedRange[1], selectedRange[3]);
      const endCol: number = Math.max(selectedRange[1], selectedRange[3]);
      if (startRow !== start.row || endRow !== end.row) {
        return null;
      }
      start.col = Math.min(startCol, start.col);
      end.col = Math.max(endCol, end.col);
    }
    let spanData: ISpanData = this.tableProvider.getSpanData(start.row, start.col);
    if (spanData && spanData.isEndPlate) {
      start.col = start.col + 1;
    }
    // end plates at the outer right edge of selection are not allowed to be merged/divided:
    let endPlateFound: boolean;
    do {
      spanData = this.tableProvider.getSpanData(start.row, end.col);
      if (spanData && spanData.isEndPlate) {
        end.col--;
        endPlateFound = true;
      } else {
        endPlateFound = false;
      }
    } while (endPlateFound && end.col > start.col);
    if (end.col <= start.col) {
      return null;
    }
    return result;
  }

  public mergeCells(): void {
    if (!this.isMergeCellsEnabled) {
      return;
    }
    const mergeAndDivideAction: MergeAndDivideAction = this.mergeAndDivideCells(this.getMergeDivideTargetCellRange(), true);
    if (mergeAndDivideAction !== null) {
      this.table.undoRedo.done(mergeAndDivideAction);
    }
  }

  public divideCells(): void {
    if (!this.isDivideCellsEnabled) {
      return;
    }
    const mergeAndDivideAction: MergeAndDivideAction = this.mergeAndDivideCells(this.getMergeDivideTargetCellRange(), false);
    if (mergeAndDivideAction !== null) {
      this.table.undoRedo.done(mergeAndDivideAction);
      if (this.isDirectPrintingModeEnabled) {
        this.afterTableChange();
      }
    }
  }

  public clearCells(): void {
    if (!this.isClearCellsEnabled) {
      return;
    }
    // as delete key not available/effective for disabled cells, so should celar cells not be available
    const selectedCells: number[][] = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected());
    const cellDataList = [];
    for (const selectedCell of selectedCells) {
      const spanData: ISpanData = this.tableProvider.getSpanData(selectedCell[0], selectedCell[1]);
      // we dont apply custom format settings to make behaviour consistent with using delete keyboard key
      // and because a complete wipe makes more sense to user, old code:
      // LabelEditorUtils.applyCustomFormatSettingsToHTML5Data(
      // LabelEditorUtils.createEmptyHTML5Data(spanData.defaultTextParameters.fontSize, spanData.defaultTextParameters.bold),
      // spanData.labelFormat.direction, null, spanData.compression, spanData.autoFormatByTextCheck)];
      // null is used instead of spanData.labelFormat.verticalAlign to make behaviour consistent with horizontalAlign

      const usesDinFont = this.usesDinFont(spanData);

      const fontName = usesDinFont ? ConfiguratorParameters.labeling.dinFontNameMiddle : ConfiguratorParameters.labeling.defaultLabelingFontName;
      const fontSize = this.getFontSizeForCell(spanData);

      const cellData = [
        selectedCell[0],
        selectedCell[1],
        LabelEditorUtils.createEmptyHTML5Data(fontSize, spanData.defaultTextParameters.bold, fontName),
      ];
      cellDataList.push(cellData);
    }
    if (cellDataList.length > 0) {
      this.table.setDataAtCell(cellDataList);
    }
  }

  private getFontSizeForCell(spanData: ISpanData): number {
    if(!isConnectionSpanData(spanData)) return spanData.getDefaultFontSize();
    const baseArticleSeries = spanData.articleOrCompositeArticle.series;
    const fontSizeBySeries = ConfiguratorParameters.labeling.directPrintingFontSizes.get(baseArticleSeries);
    return fontSizeBySeries ?? spanData.getDefaultFontSize();
  }

  private usesDinFont(spanData: ISpanData) {
    if(isConnectionSpanData(spanData)) {
      const isArticleMcsGdsLtl = this.articleContainerUtils.isOrIsPartOfMcsGdsLtl(spanData.connection.articleContainer);

      if (isArticleMcsGdsLtl && spanData.isDirectPrinting) {
        return true;
      }
    }
    return false;
  }

  public copyCells(): void {
    if (!this.isCopyCellsEnabled) {
      return;
    }
    const copyPastePlugin: any = this.table.getPlugin('copyPaste');
    copyPastePlugin.copy();
  }

  public copyFromExcel(): void {
    this.lastSelectedRangeInTableBeforeCopyFromExcel = this.table.getSelectedRange();
    const cellsSelected: boolean = LabelEditorUtils.explodeCellRanges(this.tableProvider, this.table.getSelected()).length > 0;
    if (!cellsSelected) {
      this.selectAllCells();
    }
    this.isCopyFromExcelActivated = true;
    this.alertHelper.showAlert(
      'labelEditorPopup.title.copyFromExcelAlert',
      'labelEditorPopup.label.copyFromExcelAlert',
      null,
      [AlertButton.CANCEL],
      "info",
      null,
      true,
      true
    );
  }

  private setFocusOnTable(): void {
    const copyPastePlugin: any = this.table.getPlugin('copyPaste');
    copyPastePlugin.focusableElement.focus();
  }

  private onDialogShow(): void {
    if (this.table) {
      this.table.addHook('beforeKeyDown', this.disableHandsontableShotcutsHandler);
      if (this.isCopyFromExcelActivated) {
        setTimeout(() => {
          this.setFocusOnTable();
        }, 100); // timeout needed because dialog not completely shown when event is dispatched
      }
    }
  }

  private onDialogHide(): void {
    if (this.table) {
      this.table.removeHook('beforeKeyDown', this.disableHandsontableShotcutsHandler);
      if (this.isCopyFromExcelActivated) {
        if (this.lastSelectedRangeInTableBeforeCopyFromExcel) {
          this.table.selectCells(this.lastSelectedRangeInTableBeforeCopyFromExcel, false);
        } else {
          this.table.deselectCell();
          if (this.isPasteFromExcelDone) {
            this.table.scrollViewportTo(0, 0);
          }
        }
        this.isPasteFromExcelDone = false;
        this.isCopyFromExcelActivated = false;
        this.lastSelectedRangeInTableBeforeCopyFromExcel = null;
      }
    }
  }

  private disableHandsontableShotcuts(event: KeyboardEvent): void {
    event.stopImmediatePropagation();
  }

  private shouldDeselectCellsOnOutsideClick(): boolean {
    return this.windowService.getModalsCount() === 0;
  }

  // using (clicking) on toolbar of toolbar, must not close opened label editor and should not show blue focus rectangles:
  public labelEditorToolbarMouseDown(event: MouseEvent): void {
    if (this.isLabelEditorAssistantOpen) {
      return;
    }
    if (!event.target || (event.target['id'] !== 'autoLabel' && event.target['id'] !== 'quickMarkingFrom')) {
      if (!this.isQuickMarkingFromFocused) {
        event.preventDefault(); // we still need to allow/support clicking on Auto-Label to trigger related check-box
      }
    }
    event.stopImmediatePropagation();
    if (event.target && event.target['id'] === 'quickMarkingFrom') {
      this.isQuickMarkingFromFocused = true;
    }
  }

  public close(): void {
    this.save();
    this.saveLastSymbolsProperties();
    this._cookieStorage.withPESymbolInLabelEditor = this.withPE;
    this._cookieStorage.withNSymbolInLabelEditor = this.withN;
    this._cookieStorage.lastSelectedLabelEditorTabType = this.lastSelectedTabType;
    this._cookieStorage.lastSelectedNonDirectPrintingLabelEditorTabType = this.lastSelectedNonDirectPrintingTabType;
    this._cookieStorage.lastSelectedSeparateLabelEditorTabPositionNumber = this.lastSelectedSeparateTabPositionNumber;
    this._cookieStorage.lastSelectedSeparateLabelEditorTabArticleNumber = this.lastSelectedSeparateTabArticleNumber;
    this.modeChangedEmitter.emit(LabelEditorMode.CLOSED);
  }

  private saveLastSymbolsProperties() {
    this.userSettings.setUserSettingValue("lastSelectedSymbolCharCodeInLabelEditor", this.selectedSymbol);
    this.userSettings.setUserSettingValue("lastSelectedPESymbolInLabelEditor", this.selectedPESymbol);
    this.userSettings.setUserSettingValue("lastSelectedNSymbolInLabelEditor", this.selectedNSymbol);

    this.userSettings.saveUserSettings();
  }

  private save(): void {
    const tableProviderHandsontableOperationsMap = new Map<ITableProvider, any[]>();
    for (const tableProvider of this.tableProviders()) {
      const table: any = this.tables.get(tableProvider.id);
      const handsontableOperations: any[] = table.undoRedo.doneActions.concat();
      if (ArrayUtils.isNotEmpty(handsontableOperations)) {
        tableProviderHandsontableOperationsMap.set(tableProvider, handsontableOperations);
      }
    }
    const changes: LabelEditorChanges = {
      tableProviderHandsontableOperationsMap,
    };
    if (this.markingSystemLabelModeEnabled()) {
      let selectedMarkingSystemArticle: ArticleVO = null;
      const articleTechnicalInfoBundles = this.articleTechnicalInfoBundles();
      if (
        articleTechnicalInfoBundles &&
        this.selectedMarkingSystemValue >= 0 &&
        this.selectedMarkingSystemValue <= articleTechnicalInfoBundles.length
      ) {
        selectedMarkingSystemArticle = articleTechnicalInfoBundles[this.selectedMarkingSystemValue].articleVO;
      }
      changes.selectedArticle = selectedMarkingSystemArticle;
      this.saveChangesEmitter.emit(changes);
    } else {
      changes.isDirectPrintingMode = this.isDirectPrintingModeEnabled;
      this.saveChangesEmitter.emit(changes);
    }
    this.table.clearUndo();
  }

  public cancel(): void {
    if (this.lastSelectedRangeInTable) {
      this.table.deselectCell();
    }
    const tableProviderBeforeCancel: ITableProvider = this.tableProvider;
    this.tableProviders().forEach((tableProvider) => {
      this.setCurrentTableProvider(tableProvider);
      while (this.table.isUndoAvailable()) {
        this.table.undo();
      }
      this.table.clearUndo();
    });
    this.setCurrentTableProvider(tableProviderBeforeCancel);
  }

  // custom tooltip handling:

  private afterTableOnCellMouseOver(event: MouseEvent, coords, element): void {
    this.updateTooltip(element, event);
    this.showToolTipOnMouseEnterEditor = true;
  }

  private afterTableOnCellMouseOut(event: MouseEvent, coords, element): void {
    // if (!this.tooltipContainsClientPosition(event.clientX, event.clientY)) {
    this.cancelShowingTooltip();
    // another problem in handsontable, where leaving a cell to something that is not a new/different cell,
    // does not trigger mouse over afte re-entering the same cell (solution applies to not cell-filled area of table)
    // problem can still be observed in headers
    if (this.table && this.table.view && this.table.view.activeWt) {
      this.table.view.activeWt.lastMouseOver = null;
    }
    // }
    this.showToolTipOnMouseEnterEditor = true;
  }

  public handleMouseEnterInLabelEditor(event: MouseEvent): void {
    if (this.showToolTipOnMouseEnterEditor) {
      this.updateTooltip(this.editor['container']);
    }
  }

  public handleMouseLeaveInLabelEditor(event: MouseEvent): void {
    this.updateTooltip(null);
    this.showToolTipOnMouseEnterEditor = false;
  }

  @HostListener('mousemove', ['$event'])
  public onMousemove(event: MouseEvent): void {
    if (this.isLabelEditorAssistantOpen) {
      return;
    }
    if (this.tooltipTimer.isRunning()) {
      this.tooltipTimer.reset();
      this.tooltipTimer.start();
    }
    if (!this.tooltipComponent.instance.isVisible()) {
      this.updateTooltipPosition(event.clientX, event.clientY);
    }
  }

  private onTooltipTimerComplete(): void {
    this.showTooltip();
  }

  private updateTooltip(element, event: MouseEvent = null): void {
    let tooltipText: string = null;
    if (element && element.dataset && element.dataset.errorTooltipText) {
      tooltipText = element.dataset.errorTooltipText;
    }
    if (!tooltipText && element) {
      const colHeaders = element.getElementsByClassName('colHeaderText');
      if (colHeaders && colHeaders.length > 0) {
        tooltipText = colHeaders[0].dataset.leColumnHeaderTooltipText;
      }
    }
    if (tooltipText) {
      this.prepareTooltip(event ? event.clientX : NaN, event ? event.clientY : NaN, tooltipText);
    } else {
      this.cancelShowingTooltip();
    }
  }

  private initToolTipSupport(): void {
    this.tooltipComponent = this.createToolTipComponent();
    this.tooltipTimer = new Timer(LabelEditorComponent.TOOLTIP_TIMER_DELAY);
    this.tooltipTimer.addEventListener('timer', this.onTooltipTimerCompleteHandler);
  }

  private createToolTipComponent(tooltipText: string = 'placeholder'): ComponentRef<TooltipComponent> {
    const tooltipComponent: ComponentRef<TooltipComponent> = this.viewContainerRef.createComponent(TooltipComponent);
    tooltipComponent.instance.content = tooltipText;
    tooltipComponent.instance.placement = 'bottom-right';
    tooltipComponent.instance.textAlign = 'left';
    tooltipComponent.instance.autoVisible = false;
    const rootNodes = (tooltipComponent.hostView as EmbeddedViewRef<any>).rootNodes;
    const domElem = rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
    return tooltipComponent;
  }

  private prepareTooltip(left: number, top: number, tooltipText: string): void {
    if (this.tooltipComponent) {
      this.hideTooltip();
      this.tooltipTimer.reset();
      if (!isNaN(top)) {
        this.tooltipComponent.instance.top = top + 15;
      }
      if (!isNaN(left)) {
        this.tooltipComponent.instance.left = left;
      }
      this.tooltipComponent.instance.updateTextContent(tooltipText);
      // this.tooltipComponent.instance.positionContent();
      this.tooltipTimer.start();
    }
  }

  private cancelShowingTooltip(): void {
    if (this.tooltipComponent) {
      this.hideTooltip();
      this.tooltipTimer.reset();
    }
  }

  private showTooltip(): void {
    if (this.tooltipComponent) {
      this.tooltipComponent.instance.makeVisible();
    }
  }

  private updateTooltipPosition(left: number, top: number): void {
    if (this.tooltipComponent) {
      this.tooltipComponent.instance.top = top + 15;
      this.tooltipComponent.instance.left = left;
      this.tooltipComponent.instance.positionContent();
    }
  }

  private hideTooltip(): void {
    if (this.tooltipComponent) {
      this.tooltipComponent.instance.makeTransparent();
    }
  }

  private destroyToolTip(): void {
    if (this.tooltipComponent) {
      const rootNodes = (this.tooltipComponent.hostView as EmbeddedViewRef<any>).rootNodes;
      const domElem = rootNodes[0] as HTMLElement;
      document.body.removeChild(domElem);
      this.tooltipComponent.destroy();
      this.tooltipComponent = null;
    }
  }

  private destroyToolTipSupport(): void {
    this.destroyToolTip();
    if (this.tooltipTimer) {
      this.tooltipTimer.removeEventListener('timer', this.onTooltipTimerCompleteHandler);
      this.tooltipTimer = null;
    }
  }

  private renderTable(): void {
    this.table.render();
    this.cdRef.markForCheck();
  }

  public openFavoriteArticlesOrderPopup(): Promise<void> {
    return this._userComponentPresenter.presentFavoriteArticlesOrderComponent();
  }

  private setNavigationTabsVisibility(): void {
    this.isNavigationTabsHidden = ArrayUtils.isEmpty(this.tableProviders()) || (this.tableProviders().length === 1 && !this.tableProviders()[0].table);
  }

  public onTableCreated(data: any): void {
    this.tables.set(data.identity, data.table);
  }

  private setMarkingMode(isInit: boolean): void {
    const isDirectPrintingModeAvailable: boolean = this.tableProviders().some(
      (tableProvider) => tableProvider.type === TableProviderType.DIRECT_PRINTING
    );
    const isMarkingArticlesModeAvailable: boolean = this.tableProviders().some(
      (tableProvider) => tableProvider.type !== TableProviderType.DIRECT_PRINTING
    );
    this.isChangeMarkingModeAvailable = isDirectPrintingModeAvailable && isMarkingArticlesModeAvailable;
    if (this.isChangeMarkingModeAvailable) {
      this.isDirectPrintingModeEnabled = this.shouldPreselectDirectPrintingTab(isInit);
    } else {
      this.isDirectPrintingModeEnabled = isDirectPrintingModeAvailable;
    }
  }

  private shouldPreselectDirectPrintingTab(isInit: boolean): boolean {
    return (
      this.lastSelectedTabType === TableProviderType.DIRECT_PRINTING || (isInit && IsProjectType.mcsGds(this.projectModel.currentProject))
    );
  }

  private setSelectedCellsDescriptionVisibility(): void {
    if (IsProjectType.mcs(this.projectModel.currentProject)) {
      this.isSelectedCellsDescriptionEnabled = this.userModel.user.hasPermission("VIEW_PROJECT_STRUCTURE_IN_MCS_PROJECT");
    } else if (IsProjectType.gds(this.projectModel.currentProject)) {
      this.isSelectedCellsDescriptionEnabled = this.userModel.user.hasPermission("VIEW_PROJECT_STRUCTURE_IN_GDS_PROJECT");
    } else {
      this.isSelectedCellsDescriptionEnabled = true;
    }
  }

  public isTabVisible(tableProvider: ITableProvider): boolean {
    if (this.isDirectPrintingModeEnabled) {
      return tableProvider.type === TableProviderType.DIRECT_PRINTING;
    } else {
      return tableProvider.type !== TableProviderType.DIRECT_PRINTING;
    }
  }

  public toggleDirectPrintingMode(): void {
    if (this.isUnsavedChangesExist()) {
      this.alertHelper
        .showAlert(
          'labelEditorPopup.alert.title.change_marking_method',
          'labelEditorPopup.alert.message.change_marking_method',
          null,
          [AlertButton.YES, AlertButton.NO],
          "help"
        )
        .then((answer) => {
          if (answer === AlertButton.YES) {
            this.cancel();
            this.executeToggleDirectPrintingMode();
          } else {
            this.table.selectCells(this.lastSelectedRangeInTable, false);
          }
        });
    } else {
      this.tables.forEach((table) => table.clearUndo());
      this.executeToggleDirectPrintingMode();
    }
  }

  public isUnsavedChangesExist(): boolean {
    return Array.from(this.tables.values()).some((table) => table.isUndoAvailable());
  }

  private executeToggleDirectPrintingMode(): void {
    this.isDirectPrintingModeEnabled = !this.isDirectPrintingModeEnabled;
    this.setCurrentTabIndex();
    this.selectTab(this.currentTabIndex);
  }

  private selectTab(tabIndex: number): void {
    this.setCurrentTableProvider(this.tableProviders()[tabIndex]);
    this.currentTabIndex = tabIndex;
    this.setLastSelectedTab();
    if (this.shouldSelectAllCellsOnTabChange()) {
      this.selectAllCells();
    }
  }

  private shouldSelectAllCellsOnTabChange(): boolean {
    return ArrayUtils.isNotEmpty(this.tableProvider.getUnusedLabelingSpanData());
  }

  private setCurrentTabIndex(): void {
    if (this.isDirectPrintingModeEnabled) {
      this.currentTabIndex = this.getFirstVisibleTabIndex();
    } else {
      const tabIndex: number = this.findLastSelectedNonDirectPrintingTabIndex();
      if (tabIndex === -1) {
        this.currentTabIndex = this.getFirstVisibleTabIndex();
      } else {
        this.currentTabIndex = tabIndex;
      }
    }
  }

  private findLastSelectedNonDirectPrintingTabIndex(): number {
    if (this.lastSelectedNonDirectPrintingTabType === TableProviderType.DEFAULT) {
      return this.tableProviders().findIndex((tableProvider) => tableProvider.type === this.lastSelectedNonDirectPrintingTabType);
    }

    if (this.lastSelectedNonDirectPrintingTabType === TableProviderType.SEPARATE_LABELING) {
      let tabIndex: number = this.tableProviders().findIndex((tableProvider) => {
        return (
          tableProvider.separateLabelingPositionNumber && tableProvider.separateLabelingPositionNumber === this.lastSelectedSeparateTabPositionNumber
        );
      });
      if (tabIndex === -1) {
        tabIndex = this.tableProviders().findIndex((tableProvider) => {
          return (
            tableProvider.separateLabelingArticleNumber && tableProvider.separateLabelingArticleNumber === this.lastSelectedSeparateTabArticleNumber
          );
        });
      }
      if (tabIndex === -1) {
        tabIndex = this.tableProviders().findIndex((tableProvider) => tableProvider.type === TableProviderType.SEPARATE_LABELING);
      }
      return tabIndex;
    }

    return this.tableProviders().findIndex((tableProvider) => {
      return tableProvider.tabLabel && tableProvider.tabLabel.labelingCategoryTranslateKey === this.lastSelectedNonDirectPrintingTabType;
    });
  }

  private getFirstVisibleTabIndex(): number {
    if (!this.isChangeMarkingModeAvailable) {
      return 0;
    }
    if (this.isDirectPrintingModeEnabled) {
      return this.tableProviders().findIndex((tableProvider) => tableProvider.type === TableProviderType.DIRECT_PRINTING);
    } else {
      return this.tableProviders().findIndex((tableProvider) => tableProvider.type !== TableProviderType.DIRECT_PRINTING);
    }
  }

  public onTabChange(tabIndex: number): void {
    setTimeout(() => {
      if (this.lastSelectedRangeInTable && this.currentTabIndex !== tabIndex) {
        this.table.deselectCell();
      }
      this.resetEditor();
      this.selectTab(tabIndex);
      document.getElementById('labelEditorTable_' + this.tableProvider.id).appendChild(this.labelEditor()?.nativeElement);
      this.table.render();
    }, 100);
  }

  private setCurrentTableProvider(tableProvider: ITableProvider): void {
    this.tableProvider = tableProvider;
    this.table = this.tables.get(tableProvider.id);
  }

  public ngOnDestroy(): void {
    this.notificationAsideLabelEditorSubscription.unsubscribe();
    this.destroyToolTipSupport();
    document.removeEventListener('keydown', this.documentKeyDownHandler, true);
    if (this.bsModalServiceOnShowSubscription) {
      this.bsModalServiceOnShowSubscription.unsubscribe();
    }
    if (this.bsModalServiceOnHideSubscription) {
      this.bsModalServiceOnHideSubscription.unsubscribe();
    }
  }

  public getTabLabel(tableProvider: ITableProvider): string {
    if (this.markingSystemLabelModeEnabled()) {
      return this.selectedMarkingSystemDescriptionText.split(' ', 1)[0];
    }
    if (!tableProvider || !tableProvider.tabLabel || !tableProvider.tabLabel.label) {
      return '';
    }
    if (tableProvider.tabLabel.labelingCategoryTranslateKey) {
      return this.localeResourceManager.getValueByKey('labelEditorPopup.label.labeling_category_tab_label', {
        '0': tableProvider.tabLabel.label,
        '1': this.localeResourceManager.getValueByKey(tableProvider.tabLabel.labelingCategoryTranslateKey),
      });
    } else {
      return this.localeResourceManager.getValueByKey(tableProvider.tabLabel.label);
    }
  }

  public canEditFavoriteArticles(): boolean {
    return !IsProjectType.mcsGds(this.projectModel.currentProjectType)
      && UserUtils.hasEditFavoriteArticlesPermission(this.userModel.user);
  }

  private updateTextCheckErrorsState(): void {
    if (ArrayUtils.isNotEmpty(this.tableProviders()) && this.tables.size === this.tableProviders().length) {
      this.tableProviders().forEach((tableProvider) => tableProvider.updateTextCheckErrorsState());
      this.containsTextCheckErrors = this.tableProviders().some((tableProvider) => tableProvider.containsErrors);
      this.containsTextCheckStrictConformityErrors = this.tableProviders().some((tableProvider) => tableProvider.containsStrictConformityErrors);
      if (this.containsTextCheckErrors && !this.useStrictTextCheckConformity() && this.mode !== LabelEditorMode.TEXT_CHECK_FAILED) {
        this.mode = LabelEditorMode.TEXT_CHECK_FAILED;
        this.modeChangedEmitter.emit(this.mode);
      }
      if (!this.containsTextCheckErrors && !this.useStrictTextCheckConformity() && this.mode === LabelEditorMode.TEXT_CHECK_FAILED) {
        this.mode = LabelEditorMode.APPLYING_CHANGES;
        this.modeChangedEmitter.emit(this.mode);
      }
    }
  }

  public getTabCustomClasses(tableProvider: ITableProvider): string {
    const classes: string[] = [];
    if (!this.isTabVisible(tableProvider)) {
      classes.push('hidden-item-list-tab');
    }
    if (tableProvider.containsErrors || tableProvider.containsStrictConformityErrors) {
      classes.push('contains-errors');
    }
    return classes.join(' ');
  }

  public executeUndoRedo(labelEditorUndoRedoData: LabelEditorUndoRedoData): void {
    if (!labelEditorUndoRedoData || labelEditorUndoRedoData.tableProviderIdHandsontableOperationsMap.size() === 0) {
      return;
    }
    const tableProviderBeforeUndoRedo: ITableProvider = this.tableProvider;
    labelEditorUndoRedoData.tableProviderIdHandsontableOperationsMap.forEach((tableProviderId: string, handsontableOperations: any[]) => {
      const tableProviderToProcess: ITableProvider = this.tableProviders().find((tableProvider) => tableProvider.id === tableProviderId);
      if (tableProviderToProcess) {
        this.setCurrentTableProvider(tableProviderToProcess);
        if (labelEditorUndoRedoData.isUndo) {
          this.table.undoRedo.doneActions = handsontableOperations.concat();
          while (this.table.isUndoAvailable()) {
            this.table.undo();
          }
          this.table.clearUndo();
        } else {
          this.table.undoRedo.undoneActions = handsontableOperations.concat().reverse();
          while (this.table.isRedoAvailable()) {
            this.table.redo();
          }
          this.table.clearUndo();
        }
      }
    });
    this.setCurrentTableProvider(tableProviderBeforeUndoRedo);
    this.table.selectCells(this.lastSelectedRangeInTable, false);
    this.cdRef.markForCheck();
  }

  public updateDirectPrintingTableProvider(newDirectPrintingTableProvider: ITableProvider): void {
    let oldTableProviderId: string;
    const oldTableProviderIndex: number = this.tableProviders().findIndex(
      (tableProvider: ITableProvider) => tableProvider.type === TableProviderType.DIRECT_PRINTING
    );
    if (oldTableProviderIndex !== -1) {
      const currentTableProviderId: string = this.tableProviders()[this.currentTabIndex].id;
      oldTableProviderId = this.tableProviders()[oldTableProviderIndex].id;
      this.tableProviders().splice(oldTableProviderIndex, 1);
      this.currentTabIndex = this.tableProviders().findIndex((tableProvider: ITableProvider) => tableProvider.id === currentTableProviderId);
    }
    if (newDirectPrintingTableProvider) {
      this.tableProviders().push(newDirectPrintingTableProvider);
    }
    setTimeout(() => {
      this.setMarkingMode(false);
      this.setCurrentTabIndex();
      if (this.tableProvider.id !== this.tableProviders()[this.currentTabIndex].id) {
        this.selectTab(this.currentTabIndex);
      }
      this.tables.delete(oldTableProviderId);
      this.cdRef.markForCheck();
    });
  }

  private setLastSelectedTab(): void {
    if (!this.tableProvider || !this.tableProvider.table) {
      return;
    }
    switch (this.tableProvider.type) {
      case TableProviderType.DEFAULT:
      case TableProviderType.DIRECT_PRINTING:
        this.lastSelectedTabType = this.tableProvider.type;
        break;
      case TableProviderType.SEPARATE_LABELING:
        this.lastSelectedTabType = this.tableProvider.type;
        this.lastSelectedSeparateTabPositionNumber = this.tableProvider.separateLabelingPositionNumber;
        this.lastSelectedSeparateTabArticleNumber = this.tableProvider.separateLabelingArticleNumber;
        break;
      case TableProviderType.LABELING_CATEGORY:
        this.lastSelectedTabType = this.tableProvider.tabLabel.labelingCategoryTranslateKey;
        break;
    }

    if (this.tableProvider.type !== TableProviderType.DIRECT_PRINTING) {
      this.lastSelectedNonDirectPrintingTabType = this.lastSelectedTabType;
    }
  }
}
